<template>
  <div class="mypage">
    <!-- ローディングモーダル -->
    <LoadingModal :visible="isLoading" />

    <div class="mypage-header-row">
      <div class="mypage-header">
        <h1>⚙️ マイページ</h1>
        <p>アカウント設定と学習者情報を管理しましょう</p>
      </div>
      <div class="mypage-header-actions">
        <button type="button" class="btn btn-primary add-sibling-header-btn" @click="openAddSiblingTab">
          ＋ 兄弟を追加
        </button>
      </div>
    </div>

    <!-- タブ: 1人1タブ ＋ 兄弟追加タブ（追加中のみ表示） -->
    <div v-if="learnersList.length > 0 || activeTab === 'add'" class="mypage-tabs">
      <button
        v-for="l in learnersList"
        :key="l.id"
        type="button"
        class="mypage-tab"
        :class="{ active: activeTab === l.id }"
        @click="activeTab = l.id"
      >
        {{ l.name || 'お子さま' }}
      </button>
      <button
        v-if="activeTab === 'add'"
        type="button"
        class="mypage-tab add-tab"
        :class="{ active: true }"
      >
        兄弟を追加
      </button>
    </div>

    <!-- 兄弟追加タブの内容: 情報入力 → アプリ内無料体験へ -->
    <div v-if="activeTab === 'add'" class="mypage-tab-content add-sibling-content">
      <p class="add-sibling-note">お子さまお一人につき、別途お支払いが必要です。</p>
      <form @submit.prevent="submitAddSibling" class="add-sibling-form">
        <div class="form-group">
          <label for="addSiblingName">子氏名 <span class="required">必須</span></label>
          <input id="addSiblingName" v-model="addSiblingForm.name" type="text" required placeholder="山田 花子" class="form-input" />
        </div>
        <div class="form-group">
          <label for="addSiblingKana">子氏名（ふりがな） <span class="required">必須</span></label>
          <input id="addSiblingKana" v-model="addSiblingForm.name_kana" type="text" required placeholder="やまだ はなこ" class="form-input" />
        </div>
        <div class="form-group">
          <label for="addSiblingBirthDate">生年月日 <span class="required">必須</span></label>
          <input id="addSiblingBirthDate" v-model="addSiblingForm.birth_date" type="date" required class="form-input" />
        </div>
        <div v-if="addSiblingForm.birth_date" class="form-group computed-grade">
          <label>現在の学年（自動計算）</label>
          <span class="computed-grade-value">{{ addSiblingComputedGrade }}</span>
        </div>
        <div class="form-actions">
          <button v-if="learnersList.length > 0" type="button" class="btn btn-secondary" @click="closeAddSiblingTab">キャンセル</button>
          <button type="submit" class="btn btn-primary">兄弟を登録する</button>
        </div>
      </form>
    </div>

    <!-- 選択中のお子さまのページ（同じ内容を1人分表示） -->
    <div v-else-if="currentLearner" class="collapsible-sections">
      <!-- アカウント情報セクション -->
      <div class="section-container">
        <button @click="toggleSection('account')" class="section-header">
          <div class="section-title">
            <span class="section-icon">👤</span>
            <span class="section-text">アカウント情報</span>
          </div>
          <span class="chevron" :class="{ 'rotated': expandedSections.account }">▼</span>
        </button>
        <transition name="slide">
          <div v-show="expandedSections.account" class="section-content">
            <div class="info-row">
              <label>メールアドレス</label>
              <span>{{ userProfile?.email || '読み込み中...' }}</span>
            </div>
            <div class="info-row">
              <label>お名前</label>
              <span>{{ userProfile?.full_name || '読み込み中...' }}</span>
            </div>
            <button @click="openAccountForm" class="btn btn-secondary">
              ✏️ 編集
            </button>
          </div>
        </transition>
      </div>

      <!-- 学習者設定セクション（このタブの子のみ） -->
      <div class="section-container">
        <button @click="toggleSection('learner')" class="section-header">
          <div class="section-title">
            <span class="section-icon">🎓</span>
            <span class="section-text">学習者設定</span>
          </div>
          <span class="chevron" :class="{ 'rotated': expandedSections.learner }">▼</span>
        </button>
        <transition name="slide">
          <div v-show="expandedSections.learner" class="section-content">
            <div class="learner-info">
              <div class="info-row">
                <label>学習者名</label>
                <span>{{ currentLearner.name }}</span>
              </div>
              <div class="info-row">
                <label>ふりがな</label>
                <span>{{ currentLearner.name_kana || '未設定' }}</span>
              </div>
              <div class="info-row">
                <label>生年月日</label>
                <span>{{ formatDate(currentLearner.birth_date) }}</span>
              </div>
              <div class="info-row">
                <label>現在の学年</label>
                <span>{{ currentLearnerGradeDisplay }}</span>
              </div>
              <button @click="editLearnerInfo(currentLearner)" class="btn btn-secondary">
                ✏️ 編集
              </button>
            </div>
          </div>
        </transition>
      </div>


      <!-- LINE連携セクション -->
      <div class="section-container">
        <button @click="toggleSection('line')" class="section-header">
          <div class="section-title">
            <span class="section-icon">📱</span>
            <span class="section-text">LINE連携</span>
          </div>
          <span class="chevron" :class="{ 'rotated': expandedSections.line }">▼</span>
        </button>
        
        <transition name="slide">
          <div v-show="expandedSections.line" class="section-content line-content">
            <LineIntegration :learner-id="currentLearner?.id" />
          </div>
        </transition>
      </div>

      <!-- 支払い情報セクション -->
      <div class="section-container">
        <button @click="toggleSection('payment')" class="section-header">
          <div class="section-title">
            <span class="section-icon">💳</span>
            <span class="section-text">支払い情報</span>
          </div>
          <span class="chevron" :class="{ 'rotated': expandedSections.payment }">▼</span>
        </button>
        
        <transition name="slide">
          <div v-show="expandedSections.payment" class="section-content">
            <div v-if="paymentInfo.loading" class="loading-payment">
              <p>💳 支払い情報を読み込み中...</p>
            </div>
            
            <div v-else class="payment-info">
              <div class="info-row">
                <label>現在のプラン</label>
                <span class="plan-name">{{ paymentInfo.currentPlan || '未設定' }}</span>
              </div>

              <div v-if="paymentInfo.planLineDisplay" class="info-row plan-line-row">
                <label>プランの内容</label>
                <span class="plan-line-text">{{ paymentInfo.planLineDisplay }}</span>
              </div>

              <!-- サービス停止警告 -->
              <div v-if="paymentInfo.serviceSuspended" class="alert alert-danger">
                ⚠️ サービスが一時停止中です。支払い方法を確認してください。
              </div>
              
              <!-- 決済失敗警告 -->
              <div v-if="paymentInfo.paymentFailed" class="alert alert-warning">
                ⚠️ 決済に失敗しています。支払い方法を確認してください。
              </div>

              <!-- 録音・提出（GET /api/learner-membership-status・契約状態・今日の可否・メッセージ） -->
              <div
                v-if="paymentInfo.membershipLoaded"
                class="contract-block membership-service-block"
              >
                <h4 class="contract-block-title">録音・提出の利用状況</h4>
                <div class="info-row">
                  <label>契約状態</label>
                  <span>{{ membershipStatusLabel }}</span>
                </div>
                <div class="info-row">
                  <label>今日の録音・提出</label>
                  <span
                    class="membership-submit-today"
                    :class="{
                      'is-yes': paymentInfo.membershipCanSubmitToday,
                      'is-no': !paymentInfo.membershipCanSubmitToday
                    }"
                  >
                    {{ paymentInfo.membershipCanSubmitToday ? '可能です' : 'できません' }}
                  </span>
                </div>
                <div v-if="paymentInfo.membershipMessage" class="info-row membership-message-row">
                  <label>メッセージ</label>
                  <p class="membership-message-text">{{ paymentInfo.membershipMessage }}</p>
                </div>
              </div>

              <!-- 本契約（サービス）の契約状態 -->
              <div
                v-if="paymentInfo.mainContractUi"
                class="contract-block main-contract-block"
              >
                <h4 class="contract-block-title">本契約（サービス）</h4>
                <div class="info-row">
                  <label>状態</label>
                  <span>{{ paymentInfo.mainContractUi.stateLabel }}</span>
                </div>
                <div
                  v-if="paymentInfo.mainContractUi.primaryLabel && paymentInfo.mainContractUi.primaryValue"
                  class="info-row"
                >
                  <label>{{ paymentInfo.mainContractUi.primaryLabel }}</label>
                  <span>{{ formatDate(paymentInfo.mainContractUi.primaryValue) }}</span>
                </div>
                <p
                  v-if="paymentInfo.mainContractUi.detail"
                  class="contract-detail-note"
                >
                  {{ paymentInfo.mainContractUi.detail }}
                </p>
                <div
                  v-if="showPreviewNextInvoiceButton"
                  class="preview-next-invoice-wrap"
                >
                  <button
                    type="button"
                    class="btn btn-outline btn-sm preview-next-invoice-btn"
                    :disabled="previewInvoiceLoading"
                    @click="fetchPreviewInvoice"
                  >
                    {{ previewInvoiceLoading ? '取得中...' : '現在の請求予定を確認する' }}
                  </button>
                  <p
                    v-if="previewInvoiceError"
                    class="preview-invoice-error"
                    role="alert"
                  >
                    {{ previewInvoiceError }}
                  </p>
                  <div
                    v-if="previewInvoiceResult"
                    class="preview-invoice-panel"
                  >
                    <p class="preview-invoice-note">{{ previewInvoiceResult.note }}</p>
                    <div class="info-row">
                      <label>次回請求予定額（参考）</label>
                      <span class="price"
                        >¥{{ Number(previewInvoiceResult.amount_due || 0).toLocaleString() }}</span
                      >
                    </div>
                    <div
                      v-if="previewInvoiceResult.discount_applied"
                      class="info-row"
                    >
                      <label>適用中の割引</label>
                      <span>{{
                        (previewInvoiceResult.discount_label || '割引') +
                          (Number(previewInvoiceResult.discount_amount) > 0
                            ? `（-¥${Number(
                                previewInvoiceResult.discount_amount
                              ).toLocaleString()}）`
                            : '')
                      }}</span>
                    </div>
                    <details
                      v-if="previewInvoiceResult.lines?.length"
                      class="preview-invoice-details"
                    >
                      <summary>内訳</summary>
                      <ul class="preview-invoice-lines">
                        <li
                          v-for="(ln, idx) in previewInvoiceResult.lines"
                          :key="idx"
                        >
                          <span>{{ ln.description }}</span>
                          <span class="preview-line-amount"
                            >¥{{ Number(ln.amount).toLocaleString() }}</span
                          >
                        </li>
                      </ul>
                    </details>
                  </div>
                </div>
              </div>

              <!-- 平日 / 全日プラン切替（API: /api/subscription/*） -->
              <div
                v-if="currentLearner && hasActiveSubscriptionForCurrentLearner && !paymentInfo.loading"
                class="contract-block plan-switch-block"
              >
                <template v-if="paymentInfo.subscriptionStatus === 'trialing' && !paymentInfo.isAppTrial">
                  <h4 class="contract-block-title">体験終了後のプラン</h4>
                  <p class="contract-detail-note">
                    体験中は全日でご利用いただけます。体験終了後の請求プランを選べます（いつでも変更可）。
                  </p>
                  <p v-if="paymentInfo.postTrialPlanType" class="info-row">
                    <label>現在の設定（体験終了後）</label>
                    <span>{{ planTypeJaLabel(paymentInfo.postTrialPlanType) }}プラン</span>
                  </p>
                  <div class="payment-actions plan-switch-actions">
                    <button
                      type="button"
                      class="btn btn-outline"
                      :disabled="planSwitchLoading"
                      @click="changePlanDuringTrial('weekday')"
                    >
                      体験終了後は平日プランで開始
                    </button>
                    <button
                      type="button"
                      class="btn btn-outline"
                      :disabled="planSwitchLoading"
                      @click="changePlanDuringTrial('allday')"
                    >
                      体験終了後は全日プランで開始
                    </button>
                  </div>
                </template>
                <template v-else-if="paymentInfo.subscriptionStatus === 'trialing' && paymentInfo.isAppTrial">
                  <h4 class="contract-block-title">現在無料体験中</h4>
                  <p class="contract-detail-note">この期間は全日のペースでご利用いただけます。</p>
                  <p class="contract-detail-note">
                    Day{{ trialDayCapForCurrentLearner }}の課題・練習を提出すると、下記「支払い情報」の<strong>平日プランで決済する</strong>／<strong>全日プランで決済する</strong>から、お好みのプランを選んで本契約のお手続きができるようになります。お支払い完了後、引き続きサービスをご利用いただけます。
                  </p>
                </template>
                <template v-else-if="paymentInfo.subscriptionStatus === 'active'">
                  <h4 class="contract-block-title">プラン変更（平日／全日）</h4>
                  <p class="contract-detail-note">
                    プラン変更は次回請求日から反映されます（日割り・返金はありません）。
                  </p>
                  <p v-if="paymentInfo.scheduledPlanType" class="contract-detail-note">
                    次回請求から
                    <strong>{{ planTypeJaLabel(paymentInfo.scheduledPlanType) }}プラン</strong>
                    への変更が予定されています。
                  </p>
                  <div class="payment-actions plan-switch-actions">
                    <button
                      type="button"
                      class="btn btn-outline"
                      :disabled="planSwitchLoading"
                      @click="changePlanNextCycle('weekday')"
                    >
                      平日プランに変更（次回請求から）
                    </button>
                    <button
                      type="button"
                      class="btn btn-outline"
                      :disabled="planSwitchLoading"
                      @click="changePlanNextCycle('allday')"
                    >
                      全日プランに変更（次回請求から）
                    </button>
                  </div>
                </template>
              </div>

              <div
                class="info-row"
                v-if="paymentInfo.nextInvoiceAmount != null && paymentInfo.nextInvoiceAmount !== paymentInfo.monthlyPrice"
              >
                <label>次回請求金額（内訳）</label>
                <span class="price">¥{{ paymentInfo.nextInvoiceAmount?.toLocaleString() }}</span>
              </div>
              
              <div class="info-row" v-if="paymentInfo.lastPaymentDate && paymentInfo.lastPaymentAmount">
                <label>前回決済</label>
                <span>{{ formatDate(paymentInfo.lastPaymentDate) }} - ¥{{ paymentInfo.lastPaymentAmount?.toLocaleString() }}</span>
              </div>
              
              <!-- 支払い方法詳細 -->
              <div class="info-row" v-if="paymentInfo.paymentMethod">
                <label>支払い方法</label>
                <div class="payment-method-details">
                  <span>{{ paymentInfo.paymentMethod }}</span>
                  <div v-if="paymentInfo.cardInfo?.isExpiringSoon" class="card-warning">
                    ⚠️ カードの有効期限が近づいています ({{ paymentInfo.cardInfo.expMonth }}/{{ paymentInfo.cardInfo.expYear }})
                  </div>
                </div>
              </div>
              
              <!-- プラン機能詳細 -->
              <div v-if="paymentInfo.planFeatures" class="plan-features">
                <label>プラン機能</label>
                <ul class="features-list">
                  <li v-if="paymentInfo.planFeatures.max_submissions">
                    📝 最大{{ paymentInfo.planFeatures.max_submissions }}日分の録音
                  </li>
                  <li v-if="paymentInfo.planFeatures.line_notifications">
                    📱 LINE通知機能
                  </li>
                  <li v-if="paymentInfo.planFeatures.feedback_access">
                    🎧 フィードバック音声
                  </li>
                  <li v-if="paymentInfo.planFeatures.priority_support">
                    ⭐ 優先サポート
                  </li>
                </ul>
              </div>

              <div class="payment-actions payment-actions--checkout-plans">
                <template v-if="currentLearner && showBillingRecoveryActionsForCurrentLearner">
                  <p class="contract-detail-note billing-recovery-note" role="status">
                    お支払いに問題がある可能性があります。下の「支払い方法変更」からカード情報のご確認・更新をお願いします。
                  </p>
                  <button
                    type="button"
                    class="btn btn-outline"
                    @click="changePaymentMethodNew"
                  >
                    💳 支払い方法変更
                  </button>
                </template>
                <template v-else-if="currentLearner && showPaymentButtonForCurrentLearner">
                  <button
                    type="button"
                    class="btn btn-primary"
                    :disabled="checkoutForLearnerLoading"
                    @click="goToCheckoutForCurrentLearner('weekday')"
                  >
                    {{ checkoutForLearnerLoading ? '取得中...' : '💳 平日プランで決済する' }}
                  </button>
                  <button
                    type="button"
                    class="btn btn-primary"
                    :disabled="checkoutForLearnerLoading"
                    @click="goToCheckoutForCurrentLearner('allday')"
                  >
                    {{ checkoutForLearnerLoading ? '取得中...' : '💳 全日プランで決済する' }}
                  </button>
                </template>
                <button
                  v-else-if="currentLearner && hasActiveSubscriptionForCurrentLearner"
                  class="btn btn-outline"
                  @click="changePaymentMethodNew"
                >
                  💳 支払い方法変更
                </button>
                <button class="btn btn-outline" @click="viewPaymentHistory">📄 支払い履歴</button>
                <button
                  v-if="currentLearner && canCancelCurrentLearner"
                  class="btn btn-danger"
                  type="button"
                  :disabled="cancelPrecheckLoading"
                  @click="cancelSubscriptionForLearner({ learnerId: currentLearner.id, learnerName: currentLearner.name || 'お子さま', planName: paymentInfo.currentPlan, currentPeriodEnd: paymentInfo.nextBillingDate })"
                >
                  {{ cancelPrecheckLoading ? '確認中...' : ('❌ ' + (currentLearner.name || 'お子さま') + 'の契約を解約') }}
                </button>
                <button
                  v-if="currentLearner && paymentInfo.canResume && paymentInfo.cancelAtPeriodEnd"
                  type="button"
                  class="btn btn-primary"
                  :disabled="resumeLoading"
                  @click="resumeSubscription"
                >
                  {{ resumeLoading ? '処理中...' : '🔄 再開する（解約予定を取り消す）' }}
                </button>
                <template v-else-if="currentLearner && paymentInfo.canResume && !paymentInfo.cancelAtPeriodEnd">
                  <p class="contract-detail-note" style="margin: 0.5rem 0">
                    再開時は新規契約となり、無料体験はつきません。
                  </p>
                  <div class="payment-actions">
                    <button
                      type="button"
                      class="btn btn-primary"
                      :disabled="resumeLoading"
                      @click="restartSubscriptionWithPlan('weekday')"
                    >
                      {{ resumeLoading ? '処理中...' : '平日プランで再開' }}
                    </button>
                    <button
                      type="button"
                      class="btn btn-primary"
                      :disabled="resumeLoading"
                      @click="restartSubscriptionWithPlan('allday')"
                    >
                      {{ resumeLoading ? '処理中...' : '全日プランで再開' }}
                    </button>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </transition>
      </div>
    </div>

    <!-- 独立したログアウトセクション -->
    <div class="logout-section-standalone">
      <button @click="handleLogout" class="logout-btn-standalone">
        🚪 ログアウト
      </button>
    </div>

    

    <!-- 学習者情報設定フォーム -->
    <div v-if="showLearnerForm" class="learner-form-modal">
      <div class="modal-content">
        <h3>学習者情報設定</h3>
        <form @submit.prevent="saveLearnerInfo">
          <div class="form-group">
            <label for="learnerName">学習者名</label>
            <input
              id="learnerName"
              v-model="learnerForm.name"
              type="text"
              required
              placeholder="学習者のお名前"
              class="form-input"
            />
          </div>
          
          <div class="form-group">
            <label for="learnerNameKana">ふりがな</label>
            <input
              id="learnerNameKana"
              v-model="learnerForm.name_kana"
              type="text"
              required
              placeholder="がくしゅうしゃ"
              class="form-input"
            />
          </div>
          
          <div class="form-group">
            <label for="learnerBirthDate">生年月日</label>
            <input
              id="learnerBirthDate"
              v-model="learnerForm.birth_date"
              type="date"
              required
              class="form-input"
            />
          </div>
          
          <div class="form-actions">
            <button type="button" @click="showLearnerForm = false" class="btn btn-secondary">
              キャンセル
            </button>
            <button type="submit" class="btn btn-primary">
              保存
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- アカウント情報編集モーダル -->
    <div v-if="showAccountForm" class="learner-form-modal">
      <div class="modal-content">
        <h3>アカウント情報編集</h3>
        <form @submit.prevent="saveAccountInfo">
          <div class="form-group">
            <label for="accountFullName">お名前</label>
            <input
              id="accountFullName"
              v-model="accountForm.full_name"
              type="text"
              required
              placeholder="お名前"
              class="form-input"
            />
          </div>
          <div class="form-group">
            <label for="accountEmail">メールアドレス</label>
            <input
              id="accountEmail"
              v-model="accountForm.email"
              type="email"
              required
              placeholder="example@email.com"
              class="form-input"
            />
            <small class="help-text">メールアドレス変更時は、確認メールの承認後に反映されます。</small>
          </div>
          <div class="form-actions">
            <button type="button" @click="openPasswordChangeForm" class="btn btn-secondary" :disabled="accountSaving">
              🔐 パスワード変更
            </button>
            <button type="button" @click="showAccountForm = false" class="btn btn-secondary">
              キャンセル
            </button>
            <button type="submit" class="btn btn-primary" :disabled="accountSaving">
              {{ accountSaving ? '保存中...' : '保存' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- パスワード変更モーダル -->
    <div v-if="showPasswordChangeForm" class="learner-form-modal">
      <div class="modal-content">
        <h3>パスワード変更</h3>
        <form @submit.prevent="savePasswordChange">
          <div class="form-group">
            <label for="newPassword">新しいパスワード</label>
            <input
              id="newPassword"
              v-model="passwordChangeForm.password"
              type="password"
              required
              minlength="8"
              placeholder="8文字以上で入力してください"
              class="form-input"
              autocomplete="new-password"
            />
          </div>
          <div class="form-group">
            <label for="confirmPassword">新しいパスワード（確認）</label>
            <input
              id="confirmPassword"
              v-model="passwordChangeForm.confirmPassword"
              type="password"
              required
              minlength="8"
              placeholder="確認用にもう一度入力してください"
              class="form-input"
              autocomplete="new-password"
            />
            <small class="help-text">英数字・記号を含む8文字以上を推奨します。</small>
          </div>
          <div class="form-actions">
            <button type="button" @click="showPasswordChangeForm = false" class="btn btn-secondary">
              キャンセル
            </button>
            <button type="submit" class="btn btn-primary" :disabled="passwordChangeSaving">
              {{ passwordChangeSaving ? '変更中...' : 'パスワードを変更' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- パスワード再認証モーダル（セキュリティ強化版） -->
    <div v-if="showPasswordModal" class="password-modal" @click="showPasswordModal = false">
      <div class="password-content" @click.stop>
        <div class="password-header">
          <h3>🔐 パスワード確認</h3>
          <button class="close-btn" @click="showPasswordModal = false">×</button>
        </div>
        
        <div class="password-body">
          <div class="security-notice">
            <h4>⚠️ セキュリティ確認</h4>
            <p>この操作は重要な変更です。お子様による誤操作を防ぐため、パスワードの入力が必要です。</p>
          </div>
          
          <form @submit.prevent="executePasswordAuth" class="password-form">
            <div class="form-group">
              <label for="password-confirm">現在のパスワード</label>
              <input 
                id="password-confirm"
                type="password" 
                v-model="passwordForm.password" 
                class="form-control"
                placeholder="パスワードを入力してください"
                required
                :disabled="passwordLoading"
                autocomplete="current-password"
              >
            </div>
            
            <div class="password-actions">
              <button type="button" class="btn btn-secondary" @click="showPasswordModal = false" :disabled="passwordLoading">
                キャンセル
              </button>
              <button type="submit" class="btn btn-primary" :disabled="passwordLoading || !passwordForm.password">
                {{ passwordLoading ? '確認中...' : '確認' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- プラン変更モーダル (debugmemo 9番) -->
    <div v-if="showPlanChangeModal" class="plan-change-modal" @click="showPlanChangeModal = false">
      <div class="plan-change-content" @click.stop>
        <div class="plan-change-header">
          <h3>📋 プラン変更</h3>
          <button class="close-btn" @click="showPlanChangeModal = false">×</button>
        </div>
        
        <div class="plan-change-body">
          <div v-if="planChangeLoading" class="loading-state">
            <p>⏳ プラン情報を読み込み中...</p>
          </div>
          
          <div v-else-if="availablePlans.length === 0" class="no-plans">
            <p>📋 利用可能なプランがありません</p>
          </div>
          
          <div v-else class="plans-grid">
            <div v-for="plan in availablePlans" :key="plan.id" class="plan-card">
              <div class="plan-header">
                <h4>{{ plan.plan_display_name }}</h4>
                <div class="plan-price">
                  ¥{{ plan.amount?.toLocaleString() || '0' }}
                  <span class="price-period">/月</span>
                </div>
              </div>
              
              <div class="plan-features" v-if="plan.features">
                <ul>
                  <li v-for="(value, key) in plan.features" :key="key">
                    {{ getFeatureDisplayText(key, value) }}
                  </li>
                </ul>
              </div>
              
              <div class="plan-actions">
                <button 
                  v-if="paymentInfo.planCode?.includes(plan.plan_code?.split('_')[0])"
                  class="btn btn-secondary" 
                  disabled
                >
                  現在のプラン
                </button>
                <button 
                  v-else
                  class="btn btn-primary" 
                  @click="executePlanChange(plan.stripe_price_id, plan.plan_display_name)"
                  :disabled="planChangeLoading"
                >
                  {{ planChangeLoading ? '変更中...' : 'このプランに変更' }}
                </button>
              </div>
            </div>
          </div>
          
          <div class="plan-change-notice">
            <p>⚠️ <strong>重要事項:</strong></p>
            <ul>
              <li>プラン変更は即座に反映されます</li>
              <li>日割り計算により差額が次回請求に追加/減額されます</li>
              <li>変更後のキャンセルはできませんのでご注意ください</li>
            </ul>
          </div>
        </div>
        
        <div class="plan-change-footer">
          <button class="btn btn-secondary" @click="showPlanChangeModal = false">閉じる</button>
        </div>
      </div>
    </div>

    <!-- 支払い履歴モーダル (debugmemo 8番) -->
    <div v-if="showPaymentHistory" class="payment-history-modal" @click="showPaymentHistory = false">
      <div class="payment-history-content" @click.stop>
        <div class="payment-history-header">
          <h3>📄 支払い履歴</h3>
          <button class="close-btn" @click="showPaymentHistory = false">×</button>
        </div>
        
        <div class="payment-history-body">
          <div v-if="paymentHistory.length === 0" class="no-payment-history">
            <p>🔍 支払い履歴がありません</p>
            <p class="text-muted">まだ支払いが発生していません。</p>
          </div>
          
          <div v-else class="payment-history-list">
            <div v-for="payment in paymentHistory" :key="payment.id" class="payment-history-item">
              <div class="payment-main-info">
                <div class="payment-date">
                  📅 {{ formatDate(payment.created_at) }}
                </div>
                <div class="payment-amount">
                  ¥{{ payment.amount?.toLocaleString() || '0' }}
                </div>
                <div class="payment-status" :class="payment.status">
                  {{ getStatusText(payment.status) }}
                </div>
              </div>
              
              <div class="payment-details">
                <div class="payment-description">
                  {{ payment.description || 'サブスクリプション料金' }}
                </div>
                <div class="payment-period" v-if="resolvePaymentPeriodStart(payment) && resolvePaymentPeriodEnd(payment)">
                  期間: {{ formatDate(resolvePaymentPeriodStart(payment)) }} 〜 {{ formatDate(resolvePaymentPeriodEnd(payment)) }}
                </div>
                <div class="payment-invoice" v-if="payment.invoice_pdf_url">
                  <a :href="payment.invoice_pdf_url" target="_blank" class="invoice-link">
                    📄 請求書をダウンロード
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div class="payment-history-footer">
          <button class="btn btn-secondary" @click="showPaymentHistory = false">閉じる</button>
        </div>
      </div>
    </div>

    <!-- 解約モーダル (debugmemo 10番) -->
    <div v-if="showCancelModal" class="cancel-modal" @click="showCancelModal = false">
      <div class="cancel-content" @click.stop>
        <div class="cancel-header">
          <h3 class="modal-title">❌ サブスクリプションを解約</h3>
          <button class="close-btn" @click="showCancelModal = false" aria-label="閉じる">×</button>
        </div>
        <div class="cancel-body">
          <section class="cancel-warning">
            <h4 class="warning-title">⚠️ 解約前にご確認ください</h4>
            <p class="warning-intro">解約前に、簡単なアンケートへのご協力をお願いします。</p>
            <ul class="warning-list">
              <li v-if="paymentInfo.subscriptionStatus === 'trialing' && !paymentInfo.isAppTrial">
                無料体験中に解約した場合は、その時点で学習画面はご利用いただけなくなります。
              </li>
              <li>解約後も、現在の請求期間の終了日まではご利用いただけます。</li>
              <li>録音データや学習履歴は原則削除されませんが、アクセスできなくなる場合があります。</li>
            </ul>
          </section>

          <form class="cancel-form" @submit.prevent="startCancelWithPassword">
            <div class="form-group">
              <label for="cancelReasonCategory" class="form-label">
                1. 理由（解約理由を教えてください・最も近いものを1つ）
              </label>
              <div class="select-wrapper">
                <select
                  id="cancelReasonCategory"
                  v-model="cancelForm.reasonCategory"
                  class="form-control"
                  required
                >
                  <option value="" disabled>選択してください</option>
                  <option value="reaction">① お子様の反応（やる気・英語への抵抗感など）</option>
                  <option value="continuity">② 学習の継続（習慣化・時間の確保など）</option>
                  <option value="content">③ 内容・効果（内容が合わない・効果を感じにくい）</option>
                  <option value="price">④ 料金（価格と継続のバランスなど）</option>
                  <option value="environment">⑤ 家庭の環境（部活・塾・生活リズムなど）</option>
                  <option value="goal">⑥ 目標達成・卒業（次のステップに進むなど）</option>
                  <option value="other">⑦ その他</option>
                </select>
              </div>
            </div>

            <div class="form-group animate-fade-in" v-if="cancelForm.reasonCategory && cancelForm.reasonCategory !== 'other'">
              <label for="cancelReasonDetail" class="form-label">
                2. 理由詳細（{{ cancelReasonDetailQuestions[cancelForm.reasonCategory] || 'もう少し詳しく教えてください' }}）
              </label>
              <div class="select-wrapper">
                <select
                  id="cancelReasonDetail"
                  v-model="cancelForm.reasonDetail"
                  class="form-control"
                  required
                >
                  <option value="" disabled>選択してください</option>
                  <option
                    v-for="opt in (cancelReasonDetailOptions[cancelForm.reasonCategory] || [])"
                    :key="opt"
                    :value="opt"
                  >
                    {{ opt }}
                  </option>
                </select>
              </div>
            </div>

            <div class="form-group animate-fade-in" v-if="cancelForm.reasonCategory === 'other'">
              <label for="cancelReasonDetailOther" class="form-label">
                2. 理由詳細（よろしければ理由を教えてください）
                <span class="optional-tag">任意</span>
              </label>
              <textarea
                id="cancelReasonDetailOther"
                v-model="cancelForm.reasonDetail"
                class="form-textarea"
                placeholder="解約理由や状況について、自由にご記入ください。"
                rows="4"
              ></textarea>
            </div>

            <div
              class="form-group animate-fade-in"
              v-if="(cancelForm.reasonCategory && cancelForm.reasonCategory !== 'other' && cancelForm.reasonDetail) || cancelForm.reasonCategory === 'other'"
            >
              <label for="cancelFeedback" class="form-label">
                3. ご意見・ご要望
                <span class="optional-tag">任意</span>
              </label>
              <textarea
                id="cancelFeedback"
                v-model="cancelForm.feedback"
                class="form-textarea"
                placeholder="サービス改善のため、よろしければご意見をお聞かせください。"
                rows="4"
              ></textarea>
            </div>

            <div class="cancel-actions">
              <button
                type="button"
                class="btn-secondary-full"
                @click="showCancelModal = false"
                :disabled="cancelLoading || passwordLoading"
              >
                戻る
              </button>
              <button
                type="submit"
                class="btn-danger-full"
                :disabled="cancelLoading || passwordLoading"
              >
                <span v-if="cancelLoading || passwordLoading">解約処理中...</span>
                <span v-else>解約する</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { supabase } from '../supabaseClient'
import { getApiBase } from '../utils/apiBase.js'
import { getGradeFromBirthDate } from '../utils/gradeFromBirthDate.js'
import { useSelectedLearner } from '../composables/useSelectedLearner.js'
import { invalidateFamilyMembership } from '../composables/useFamilyMembership.js'
import LineIntegration from '../components/LineIntegration.vue'
import LoadingModal from '../components/common/LoadingModal.vue'

const route = useRoute()
const router = useRouter()
const { loadLearners, setSelectedLearner, selectedLearnerId } = useSelectedLearner()
const isLoading = ref(true)
const RESYNC_INTERVAL_MS = 5 * 60 * 1000
const lastResyncAt = ref(0)
const lastResyncYmd = ref('')
let isResyncing = false
const userProfile = ref(null)
const learnersList = ref([])
const learnerInfo = ref(null) // 編集フォーム用
const showLearnerForm = ref(false)
const showAccountForm = ref(false)
const accountSaving = ref(false)
const accountForm = ref({
  full_name: '',
  email: ''
})
const showPasswordChangeForm = ref(false)
const passwordChangeSaving = ref(false)
const passwordChangeForm = ref({
  password: '',
  confirmPassword: ''
})
const addSiblingForm = ref({ name: '', name_kana: '', birth_date: '' })
const addSiblingComputedGrade = computed(() => getGradeFromBirthDate(addSiblingForm.value.birth_date))
const currentLearnerGradeDisplay = computed(() =>
  currentLearner.value?.birth_date ? getGradeFromBirthDate(currentLearner.value.birth_date) : '—'
)

// タブ: 学習者ID または 'add'（兄弟追加タブ）
const activeTab = ref(null)
const currentLearner = computed(() =>
  activeTab.value && activeTab.value !== 'add'
    ? learnersList.value.find((l) => l.id === activeTab.value) || null
    : null
)

// 選択中のお子さまが active/trialing のサブスクを持っているか（支払方法変更ボタン表示用）
const hasActiveSubscriptionForCurrentLearner = computed(() =>
  ['active', 'trialing'].includes(paymentInfo.value?.subscriptionStatus))

// past_due 等で「新規決済」ではなく支払い修正が必要な状態（active/trialing かつ paymentFailed は既存の支払い方法変更でカバー）
const needsBillingRecoveryUi = computed(() => {
  if (paymentInfo.value?.loading) return false
  const st = paymentInfo.value?.subscriptionStatus
  if (['past_due', 'unpaid', 'incomplete'].includes(st)) return true
  if (paymentInfo.value?.paymentFailed) return true
  if (paymentInfo.value?.serviceSuspended) return true
  return false
})

const showBillingRecoveryActionsForCurrentLearner = computed(
  () =>
    !!currentLearner.value &&
    needsBillingRecoveryUi.value &&
    !hasActiveSubscriptionForCurrentLearner.value
)

// 次回請求日付近に表示: preview API はボタン押下時のみ（過去未払いも確認したい場合は past_due を含む）
const showPreviewNextInvoiceButton = computed(() => {
  if (!currentLearner.value || paymentInfo.value.loading) return false
  if (!paymentInfo.value.nextBillingDate) return false
  // App 内無料体験（Stripe 契約なし）では preview は成立しない可能性があるため非表示
  if (paymentInfo.value.isAppTrial) return false
  return ['active', 'trialing', 'past_due'].includes(paymentInfo.value.subscriptionStatus)
})

// 無料体験の上限Day。trial_version==='7day_v1'ならDay7、それ以外（従来の14日版）ならDay14。
// backend側 advance_current_day_if_eligible_for_learner の day_cap判定と揃える。
const trialDayCapForCurrentLearner = computed(() => (
  currentLearner.value?.trial_version === '7day_v1' ? 7 : 14
))

// 「💳 決済する」= 14日体験付きの新規Checkout（兄弟初回・初めての利用向け）
// 一度解約した学習者は API が canResume / canceled を返すので、再開フローのみ（再開する）とし決済するは出さない
// アプリ内無料体験中でも、上限Day（7日版はDay7、14日版はDay14）の課題・練習提出まで到達していれば、
// 体験終了(カレンダー上限)を待たずに決済ボタンを出す
// （Topアクセス自体はカレンダー上限まで維持するが、支払いは上限Day到達時点から可能にする）
const showPaymentButtonForCurrentLearner = computed(() => {
  if (!currentLearner.value) return false
  const isAppTrialAtDay14Plus =
    paymentInfo.value?.isAppTrial === true &&
    (currentLearner.value.current_day ?? 1) >= trialDayCapForCurrentLearner.value
  if (hasActiveSubscriptionForCurrentLearner.value && !isAppTrialAtDay14Plus) return false
  if (needsBillingRecoveryUi.value) return false
  if (paymentInfo.value?.canResume) return false
  const st = paymentInfo.value?.subscriptionStatus
  if (st === 'canceled') return false
  return true
})

// 選択中タブのお子さまに紐づくサブスク一覧（解約ボタン用）
const currentLearnerSubscriptions = computed(() => {
  if (!currentLearner.value) return []
  const id = currentLearner.value.id
  return subscriptionList.value.filter(
    (s) => s.learnerId === id || (s.learnerId == null && learnersList.value[0]?.id === id)
  )
})

// 解約ボタン表示（表示中タブの支払い情報ベース）
// すでに「解約予定（cancelAtPeriodEnd=true）」になっている場合は、再開ボタンのみ表示し、解約ボタンは非表示にする
const canCancelCurrentLearner = computed(() => {
  const st = paymentInfo.value?.subscriptionStatus
  const cancelAtPeriodEnd = paymentInfo.value?.cancelAtPeriodEnd
  if (cancelAtPeriodEnd) return false
  if (paymentInfo.value.isAppTrial) return false
  return ['trialing', 'active', 'past_due', 'unpaid', 'incomplete'].includes(st)
})

/** learner-membership-status 用の初期値（学習者未選択時など） */
const emptyMembershipFields = () => ({
  membershipLoaded: false,
  membershipStatus: null,
  membershipPlanType: null,
  membershipCanAccess: null,
  membershipCanSubmitToday: null,
  membershipMessage: null,
  membershipIsHoliday: null,
  membershipScheduledPlanType: null,
  membershipPostTrialPlanType: null
})

/** 契約状態の表示ラベル（新APIの status / plan_type） */
const membershipStatusLabel = computed(() => {
  if (!paymentInfo.value?.membershipLoaded) return '—'
  const st = paymentInfo.value?.membershipStatus
  const pt = paymentInfo.value?.membershipPlanType
  const planJa =
    pt === 'allday' ? '全日' : pt === 'weekday' ? '平日' : pt ? String(pt) : ''
  const stMap = {
    trialing: '体験中',
    active: '本契約・有効',
    past_due: 'お支払いの確認が必要',
    canceled: '解約済み',
    unpaid: '未払い',
    incomplete: '契約未完了',
    inactive: '未契約',
    paused: '一時停止'
  }
  const stJa = stMap[st] || st || ''
  const parts = [stJa, planJa].filter(Boolean)
  return parts.length ? parts.join('・') : '—'
})

/**
 * GET /api/learner-membership-status（料金・次回請求は subscription-info-v2 のまま）
 */
async function fetchLearnerMembershipFields(learnerId, accessToken) {
  const empty = emptyMembershipFields()
  if (!learnerId || learnerId === 'add') return empty
  try {
    const base = getApiBase()
    const url = `${base ? base + '/' : ''}api/learner-membership-status?learner_id=${encodeURIComponent(learnerId)}`
    const res = await fetch(url, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      }
    })
    if (!res.ok) {
      return {
        membershipLoaded: true,
        membershipStatus: null,
        membershipPlanType: null,
        membershipCanAccess: false,
        membershipCanSubmitToday: false,
        membershipMessage: '利用状況を取得できませんでした。',
        membershipIsHoliday: null,
        membershipScheduledPlanType: null,
        membershipPostTrialPlanType: null
      }
    }
    const m = await res.json()
    return {
      membershipLoaded: true,
      membershipStatus: m.status ?? null,
      membershipPlanType: m.plan_type ?? null,
      membershipCanAccess: !!m.can_access,
      membershipCanSubmitToday: !!m.can_submit_today,
      membershipMessage: (m.message && String(m.message).trim()) || '',
      membershipIsHoliday: !!m.is_holiday,
      membershipScheduledPlanType: m.scheduled_plan_type || null,
      membershipPostTrialPlanType: m.post_trial_plan_type || null
    }
  } catch (e) {
    console.warn('learner-membership-status 取得エラー:', e)
    return {
      membershipLoaded: true,
      membershipStatus: null,
      membershipPlanType: null,
      membershipCanAccess: false,
      membershipCanSubmitToday: false,
      membershipMessage: '利用状況を取得できませんでした。',
      membershipIsHoliday: null,
      membershipScheduledPlanType: null,
      membershipPostTrialPlanType: null
    }
  }
}

const openAddSiblingTab = () => {
  activeTab.value = 'add'
}
const closeAddSiblingTab = () => {
  activeTab.value = learnersList.value.length > 0 ? learnersList.value[0].id : 'add'
}
const lineInfo = ref({
  parentLinked: false,
  learnerLinked: false
})

// 支払い情報
// 支払い履歴関連
const paymentHistory = ref([])
const showPaymentHistory = ref(false)

/** 請求 preview（ボタン押下時のみ。paymentInfo とは独立） */
const previewInvoiceLoading = ref(false)
const previewInvoiceResult = ref(null)
const previewInvoiceError = ref('')
const PREVIEW_INVOICE_ERR =
  '現在の請求予定を取得できませんでした。しばらくしてからもう一度お試しください。'

const paymentInfo = ref({
  loading: true,
  currentPlan: null,
  monthlyPrice: null,
  nextBillingDate: null,
  paymentMethod: null,
  subscriptionStatus: null,
  trialEnd: null,
  // App 内無料体験（Stripe 契約なし）の場合は true
  isAppTrial: false,
  // 'stripe' | 'app'（backend が返す）
  trialSource: null,
  // Stripe 実態に基づく表示（標準プラン定価・割引）
  planLineDisplay: null,
  catalogMonthlyPrice: null,
  discountApplied: false,
  discountLabel: null,
  // 本契約の請求期間（subscription-info-v2）
  mainContractPeriodStart: null,
  mainContractPeriodEnd: null,
  canResume: false,
  cancelAtPeriodEnd: false,
  // API: subscription-info-v2 が返す契約UI（本契約）
  mainContractUi: null,
  // GET /api/learner-membership-status（学習者タブ時のみ）
  membershipLoaded: false,
  membershipStatus: null,
  membershipPlanType: null,
  membershipCanAccess: null,
  membershipCanSubmitToday: null,
  membershipMessage: null,
  membershipIsHoliday: null,
  membershipScheduledPlanType: null,
  membershipPostTrialPlanType: null,
  scheduledPlanType: null,
  postTrialPlanType: null
})

// 折り畳みセクション管理（初期は全て閉じている）
const expandedSections = ref({
  account: false,
  learner: false,
  line: false,
  payment: false
})

// セクションの開閉切り替え
const toggleSection = (sectionName) => {
  expandedSections.value[sectionName] = !expandedSections.value[sectionName]
  
  // ローカルストレージに状態を保存
  localStorage.setItem('mypage-sections', JSON.stringify(expandedSections.value))
}



const learnerForm = ref({
  name: '',
  name_kana: '',
  birth_date: ''
})

// フォールバックプロフィール設定
const createFallbackProfile = async (user) => {
  console.log('🛡️ フォールバックプロフィール設定')
  userProfile.value = {
    id: user.id,
    email: user.email,
    full_name: user.user_metadata?.full_name || user.email?.split('@')[0] || 'ユーザー',
    role: 'adult',
    subscription_status: 'unknown'
  }
}

// Authメールと users_phase2.email の差分を吸収（メール変更承認後の表示ズレ対策）
const syncProfileEmailWithAuth = async (user, profileEmail = null) => {
  try {
    const authEmail = (user?.email || '').trim()
    const dbEmail = (profileEmail || '').trim()
    if (!user?.id || !authEmail) return
    if (dbEmail && dbEmail.toLowerCase() === authEmail.toLowerCase()) return

    const { error } = await supabase
      .from('users_phase2')
      .update({
        email: authEmail,
        updated_at: new Date().toISOString()
      })
      .eq('id', user.id)

    if (error) {
      console.warn('⚠️ users_phase2 email 同期失敗:', error)
      return
    }
    if (userProfile.value) {
      userProfile.value = { ...userProfile.value, email: authEmail }
    }
    console.log('✅ users_phase2 email を Auth と同期しました:', { userId: user.id, email: authEmail })
  } catch (e) {
    console.warn('⚠️ email 同期処理エラー:', e)
  }
}

// 初期プロフィール作成
const createInitialProfile = async (user) => {
  try {
    console.log('🆕 初期プロフィール作成開始:', user.id)
    
    // 最小限のプロフィール情報のみ
    const initialProfile = {
      id: user.id,
      email: user.email,
      full_name: user.user_metadata?.full_name || 'ユーザー',
      role: 'adult'
      // birth_date は除外（スキーマキャッシュ問題回避）
    }
    
    const { data, error } = await supabase
      .from('users_phase2')
      .insert([initialProfile])
      .select()
    
    if (error) {
      console.error('❌ 初期プロフィール作成エラー:', error)
      // プロフィール作成に失敗してもフォールバック情報を設定
      userProfile.value = {
        id: user.id,
        email: user.email,
        full_name: user.user_metadata?.full_name || 'ユーザー',
        role: 'adult'
      }
    } else if (data && data.length > 0) {
      userProfile.value = data[0]
      console.log('✅ 初期プロフィール作成完了:', data[0])
    }
  } catch (error) {
    console.error('❌ プロフィール作成例外:', error)
    // 完全なフォールバック
    userProfile.value = {
      id: user.id,
      email: user.email,
      full_name: user.user_metadata?.full_name || 'ユーザー',
      role: 'adult'
    }
  }
}

// ログアウト機能
const handleLogout = async () => {
  if (confirm('ログアウトしますか？')) {
    try {
      await supabase.auth.signOut()
      router.push('/')
    } catch (error) {
      console.error('ログアウトエラー:', error)
      alert('ログアウトに失敗しました')
    }
  }
}

// 支払い情報を読み込む関数（ユーザー情報を引数で受け取る / learnerId でその子のサブスク）
const loadPaymentInfoWithUser = async (user, learnerId = null) => {
  try {
    paymentInfo.value.loading = true

    if (!user?.id) return

    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'https://api.otoeigo.com'
    const session = await supabase.auth.getSession()
    const accessToken = session.data.session?.access_token

    if (!accessToken) {
      console.error('認証トークンが見つかりません')
      return
    }

    const url = learnerId
      ? `${apiBaseUrl}/api/subscription-info-v2?learner_id=${encodeURIComponent(learnerId)}`
      : `${apiBaseUrl}/api/subscription-info-v2`
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      }
    })

    if (response.ok) {
      const data = await response.json()
      console.log('支払い情報取得成功:', data)
      if (!learnerId) await loadSubscriptionList()

      const mwm = await fetchLearnerMembershipFields(learnerId, accessToken)

      paymentInfo.value = {
        loading: false,
        currentPlan: data.currentPlan || 'プランなし',
        planCode: data.planCode,
        planLineDisplay: data.planLineDisplay || null,
        catalogMonthlyPrice: data.catalogMonthlyPrice ?? null,
        discountApplied: !!data.discountApplied,
        discountLabel: data.discountLabel || null,
        monthlyPrice: data.monthlyPrice,
        nextBillingDate: data.nextBillingDate,
        lastPaymentAmount: data.lastPaymentAmount,
        lastPaymentDate: data.lastPaymentDate,
        nextInvoiceAmount: data.nextInvoiceAmount,
        paymentMethod: data.paymentMethod,
        cardInfo: data.cardInfo,
        subscriptionStatus: data.subscriptionStatus || 'inactive',
        trialEnd: data.trialEnd,
        isAppTrial: !!data.isAppTrial,
        trialSource: data.trialSource || null,
        paymentFailed: data.paymentFailed || false,
        serviceSuspended: data.serviceSuspended || false,
        serviceStatus: data.serviceStatus || 'active',
        planFeatures: data.planFeatures,
        stripeCustomerId: data.stripeCustomerId,
        stripeSubscriptionId: data.stripeSubscriptionId,
        // 再開可否（解約済み・解約予定時）
        canResume: !!data.canResume,
        cancelAtPeriodEnd: !!data.cancelAtPeriodEnd,
        mainContractPeriodStart: data.currentPeriodStart || null,
        mainContractPeriodEnd: data.currentPeriodEnd || null,
        mainContractUi: data.mainContractUi || null,
        ...mwm,
        scheduledPlanType: data.scheduledPlanType ?? mwm.membershipScheduledPlanType ?? null,
        postTrialPlanType: data.postTrialPlanType ?? mwm.membershipPostTrialPlanType ?? null
      }
    } else {
      console.error('支払い情報の取得に失敗:', response.status)
      paymentInfo.value = {
        ...paymentInfo.value,
        loading: false,
        isAppTrial: false,
        trialSource: null,
        ...emptyMembershipFields()
      }
    }
  } catch (error) {
    console.error('支払い情報読み込みエラー:', error)
    paymentInfo.value = {
      ...paymentInfo.value,
      loading: false,
      isAppTrial: false,
      trialSource: null,
      ...emptyMembershipFields()
    }
  }
}

/** subscription-info-v2 は loadPaymentInfoWithUser へ集約（同一処理の二重実装・二重 fetch を防ぐ） */
const loadPaymentInfo = async (learnerId = null) => {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user?.id) return
  await loadPaymentInfoWithUser(user, learnerId)
}

// 支払い履歴表示
const viewPaymentHistory = async () => {
  try {
    console.log('📄 支払い履歴表示を開始します')
    
    const { data: { session } } = await supabase.auth.getSession()
    const token = session?.access_token
    
    if (!token) {
      alert('認証が必要です。ログインしてください。')
      return
    }

    // 支払い履歴を取得（選択中の学習者ごと）
    const learnerId = currentLearner?.value?.id || null
    const baseUrl = `${import.meta.env.VITE_API_BASE_URL}/api/payment-history`
    const url = learnerId ? `${baseUrl}?learner_id=${encodeURIComponent(learnerId)}` : baseUrl
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || '支払い履歴の取得に失敗しました')
    }

    const data = await response.json()
    console.log('✅ 支払い履歴取得成功:', data)

    // 支払い履歴データを設定
    paymentHistory.value = data.payments || []
    showPaymentHistory.value = true

  } catch (error) {
    console.error('❌ 支払い履歴取得エラー:', error)
    alert(`支払い履歴の取得でエラーが発生しました: ${error.message}`)
  }
}

/** 次回請求の Stripe preview（オンデマンドのみ。paymentInfo 取得とは独立） */
async function fetchPreviewInvoice() {
  previewInvoiceLoading.value = true
  previewInvoiceError.value = ''
  previewInvoiceResult.value = null
  try {
    const { data: { session } } = await supabase.auth.getSession()
    const token = session?.access_token
    if (!token) {
      throw new Error('認証情報がありません')
    }

    const learnerId = selectedLearnerId.value
    if (!learnerId) {
      throw new Error('学習者が選択されていません')
    }

    const base = getApiBase()
    const url = `${base ? base + '/' : ''}api/subscription/preview-next-invoice`

    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ learner_id: learnerId })
    })

    if (!res.ok) {
      const text = await res.text().catch(() => '')
      throw new Error(text || '請求予定の取得に失敗しました')
    }

    const json = await res.json()
    if (!json.ok) {
      throw new Error(json.error || '請求予定の取得に失敗しました')
    }
    previewInvoiceResult.value = json
  } catch (e) {
    console.error('preview invoice fetch error:', e)
    previewInvoiceResult.value = null
    previewInvoiceError.value = PREVIEW_INVOICE_ERR
  } finally {
    previewInvoiceLoading.value = false
  }
}

// パスワード再認証関連のリアクティブデータ
const showPasswordModal = ref(false)
const passwordForm = ref({ password: '' })
const passwordLoading = ref(false)
const pendingAction = ref(null) // 認証後に実行する操作

// プラン変更関連のリアクティブデータ
const availablePlans = ref([])
const showPlanChangeModal = ref(false)
const planChangeLoading = ref(false)

// プラン変更モーダルを開く
const changePlan = async () => {
  try {
    console.log('📋 プラン変更モーダルを開きます')
    
    const { data: { session } } = await supabase.auth.getSession()
    const token = session?.access_token
    
    if (!token) {
      alert('認証が必要です。ログインしてください。')
      return
    }

    planChangeLoading.value = true

    // 利用可能なプラン一覧を取得
    const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/available-plans`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || 'プラン情報の取得に失敗しました')
    }

    const data = await response.json()
    console.log('✅ プラン一覧取得成功:', data)

    availablePlans.value = data.plans || []
    showPlanChangeModal.value = true

  } catch (error) {
    console.error('❌ プラン変更モーダル表示エラー:', error)
    alert(`プラン情報の取得でエラーが発生しました: ${error.message}`)
  } finally {
    planChangeLoading.value = false
  }
}

// プラン変更実行（パスワード再認証版）
const executePlanChange = (newPriceId, planName) => {
  requirePasswordAuth('change_plan', { newPriceId, planName })
}

// プラン変更実行（認証後）
const executeChangePlan = async (actionData) => {
  try {
    const { newPriceId, planName } = actionData
    console.log('🔄 プラン変更実行（認証済み）:', { newPriceId, planName })
    
    // より詳細な確認ダイアログ
    const currentDate = new Date()
    const nextBillingDate = paymentInfo.value.nextBillingDate ? new Date(paymentInfo.value.nextBillingDate) : new Date()
    const daysUntilNextBilling = Math.ceil((nextBillingDate - currentDate) / (1000 * 60 * 60 * 24))
    
    const confirmMessage = `${planName}に変更しますか？\n\n📋 変更内容:\n• 新プランは即座に有効になります\n• 日割り計算により、次回請求（${daysUntilNextBilling}日後）で差額を清算します\n• 変更後のキャンセルはできません`
    
    if (!confirm(confirmMessage)) {
      return
    }

    const { data: { session } } = await supabase.auth.getSession()
    const token = session?.access_token
    
    if (!token) {
      alert('認証が必要です。ログインしてください。')
      return
    }

    planChangeLoading.value = true

    // プラン変更API呼び出し
    const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/change-plan`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ newPriceId })
    })
    invalidateFamilyMembership() // 契約・体験・学習者が変わったので、家族の利用可否の結果を捨てる（指示書 B）

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || 'プラン変更に失敗しました')
    }

    const data = await response.json()
    console.log('✅ プラン変更成功:', data)

    // 成功メッセージ
    alert(`🎉 ${planName}への変更が完了しました！\n\n変更内容はまもなく反映されます。`)
    
    // モーダルを閉じて支払い情報を再読み込み
    showPlanChangeModal.value = false
    await loadPaymentInfo()

  } catch (error) {
    console.error('❌ プラン変更エラー:', error)
    alert(`プラン変更でエラーが発生しました: ${error.message}`)
  } finally {
    planChangeLoading.value = false
  }
}

// パスワード再認証機能
const requirePasswordAuth = (actionType, actionData = null) => {
  console.log('🔐 パスワード再認証が必要:', actionType)
  pendingAction.value = { type: actionType, data: actionData }
  passwordForm.value = { password: '' }
  showPasswordModal.value = true
}

const executePasswordAuth = async () => {
  try {
    passwordLoading.value = true
    
    const { data: { user } } = await supabase.auth.getUser()
    if (!user?.email) {
      throw new Error('ユーザー情報の取得に失敗しました')
    }

    // パスワード再認証
    const { error } = await supabase.auth.signInWithPassword({
      email: user.email,
      password: passwordForm.value.password
    })

    if (error) {
      throw new Error('パスワードが正しくありません')
    }

    console.log('✅ パスワード認証成功')
    
    // 認証成功後、保留中の操作を実行
    const action = pendingAction.value
    showPasswordModal.value = false
    
    if (action) {
      switch (action.type) {
        case 'change_payment_method':
          await executeChangePaymentMethod()
          break
        case 'change_plan':
          await executeChangePlan(action.data)
          break
        case 'cancel_subscription':
          await executeCancelSubscriptionDirect(action.data)
          break
        default:
          console.warn('未知のアクション:', action.type)
      }
    }

  } catch (error) {
    console.error('❌ パスワード認証エラー:', error)
    alert(`認証に失敗しました: ${error.message}`)
  } finally {
    passwordLoading.value = false
  }
}

// 解約関連のリアクティブデータ（兄弟対応: どの子の契約を解約するか）
const subscriptionList = ref([])
const showCancelModal = ref(false)
const cancelLoading = ref(false)
const cancelPrecheckLoading = ref(false)
const resumeLoading = ref(false)
const planSwitchLoading = ref(false)
const cancelForm = ref({
  reasonCategory: '',
  reasonDetail: '',
  feedback: ''
})
const cancelTarget = ref(null)

// 解約アンケート: Q2 の選択肢
const cancelReasonDetailOptions = {
  reaction: [
    '英語自体に苦手意識があった',
    '最初はやったが、途中でやる気がなくなった',
    '親が声をかけてもやりたがらなかった',
    '思春期で親の勧めに反発した',
    '他のこと（ゲーム・YouTubeなど）を優先した',
    'その他'
  ],
  continuity: [
    '毎日やる時間を決められなかった',
    '生活の中にうまく組み込めなかった',
    '子どもが忘れてしまうことが多かった',
    '親の声掛けが難しかった',
    '最初のルール作りがうまくいかなかった',
    'その他'
  ],
  content: [
    '動画の内容が子どもに合わなかった',
    '学習方法が合わなかった',
    '成果が見えにくかった',
    '英語力が伸びている実感が持てなかった',
    '期待していた内容と違った',
    'その他'
  ],
  price: [
    '内容は良いが家計的に継続が難しい',
    '価格と内容のバランスが合わないと感じた',
    '他の教育費（塾など）を優先することになった',
    '短期間だけ利用するつもりだった',
    'その他'
  ],
  environment: [
    '部活が忙しくなった',
    '塾が忙しくなった',
    '他の習い事を優先することになった',
    '家庭の生活リズムに合わなかった',
    'スマホや端末の利用ルールの問題',
    'その他'
  ],
  goal: [
    '英語を毎日やる習慣ができた',
    '英語への抵抗感がなくなった',
    '英語が好きになった',
    '英検など次の学習に進むことになった',
    '別の英語学習にステップアップした',
    'その他'
  ]
}

const cancelReasonDetailQuestions = {
  reaction: 'Q2. お子様のどのような反応が一番近かったですか？',
  continuity: 'Q2. 習慣化が難しかった主な理由は何ですか？',
  content: 'Q2. どの点が合わないと感じましたか？',
  price: 'Q2. どちらに近いですか？',
  environment: 'Q2. 主な理由を教えてください。',
  goal: 'Q2. どのような変化がありましたか？'
}

// 兄弟ごとの解約対象一覧を取得（支払い情報と同時に利用）
const loadSubscriptionList = async () => {
  try {
    const { data: { session } } = await supabase.auth.getSession()
    const token = session?.access_token
    if (!token) return
    const res = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/subscription-list`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
    if (!res.ok) return
    const data = await res.json()
    subscriptionList.value = data.subscriptions || []
  } catch (e) {
    console.warn('subscription-list 取得失敗:', e)
    subscriptionList.value = []
  }
}

// 解約開始: 事前チェック後にアンケートモーダルを開く
const cancelSubscriptionForLearner = async (item) => {
  cancelPrecheckLoading.value = true
  try {
    const { data: { session } } = await supabase.auth.getSession()
    const token = session?.access_token
    if (!token) {
      alert('認証が必要です。ログインしてください。')
      return
    }
    const learnerId = item?.learnerId
    if (!learnerId) return
    const apiBase = import.meta.env.VITE_API_BASE_URL || ''
    const res = await fetch(
      `${apiBase}/api/cancel-subscription-precheck?learner_id=${encodeURIComponent(learnerId)}`,
      { headers: { Authorization: `Bearer ${token}` } }
    )
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      alert(data.error || '解約前の確認に失敗しました。')
      return
    }
    cancelTarget.value = item
    cancelForm.value = { reasonCategory: '', reasonDetail: '', feedback: '' }
    showCancelModal.value = true
  } catch (e) {
    console.error('cancel-subscription-precheck:', e)
    alert('解約前の確認に失敗しました。')
  } finally {
    cancelPrecheckLoading.value = false
  }
}

// 解約モーダルの「解約する」ボタン → パスワード再認証を要求
const startCancelWithPassword = () => {
  if (!cancelTarget.value?.learnerId) {
    alert('解約対象のお子さまが選択されていません。')
    return
  }
  // アンケート入力後は一度解約モーダルを閉じてから、パスワード確認モーダルへ遷移する
  showCancelModal.value = false
  requirePasswordAuth('cancel_subscription', { learnerId: cancelTarget.value.learnerId })
}

// パスワード認証後に直接 /api/cancel-subscription を呼び出して解約
const executeCancelSubscriptionDirect = async (actionData) => {
  try {
    const learnerId = actionData?.learnerId || cancelTarget.value?.learnerId || null
    if (!learnerId) {
      throw new Error('解約対象のお子さまが特定できませんでした。')
    }

    cancelLoading.value = true

    const { data: { session } } = await supabase.auth.getSession()
    const token = session?.access_token
    if (!token) {
      throw new Error('認証が必要です。ログインし直してください。')
    }

    const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/cancel-subscription`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        reason: `${cancelForm.value.reasonCategory || 'unspecified'}::${cancelForm.value.reasonDetail || 'unspecified'}`,
        feedback: cancelForm.value.feedback || '',
        learner_id: learnerId
      })
    })

    const data = await response.json().catch(() => ({}))
    if (!response.ok || !data.success) {
      throw new Error(data.error || 'サブスクリプションの解約に失敗しました')
    }

    if (paymentInfo.value?.subscriptionStatus === 'trialing' && !paymentInfo.value?.isAppTrial) {
      alert('解約手続きが完了しました。無料体験の解約のため、この時点で学習画面はご利用いただけなくなります。')
    } else {
      alert('解約手続きが完了しました。現在の請求期間の終了日まではご利用いただけます。')
    }
    showCancelModal.value = false
    cancelTarget.value = null

    // 支払い情報・解約対象一覧を再読み込み
    if (currentLearner.value?.id) {
      await loadPaymentInfo(currentLearner.value.id)
    } else {
      await loadPaymentInfo()
    }
    await loadSubscriptionList()
  } catch (error) {
    console.error('❌ 直接解約エラー:', error)
    alert(`解約処理でエラーが発生しました: ${error.message}`)
  } finally {
    cancelLoading.value = false
  }
}

// 退会後の再開（解約予定の取り消し or 解約済みの場合は Checkout へ）
const resumeSubscription = async () => {
  const learner = currentLearner.value
  if (!learner?.id) {
    alert('お子さまを選択してください。')
    return
  }
  resumeLoading.value = true
  try {
    const { data: { session } } = await supabase.auth.getSession()
    const token = session?.access_token
    if (!token) {
      alert('ログインが必要です。')
      return
    }
    const apiBase = getApiBase()
    const res = await fetch(`${apiBase}/api/resume-subscription`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ learner_id: learner.id })
    })
    invalidateFamilyMembership() // 契約・体験・学習者が変わったので、家族の利用可否の結果を捨てる（指示書 B）
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      alert(data.error || '再開処理に失敗しました。')
      return
    }
    if (data.checkoutUrl) {
      window.location.href = data.checkoutUrl
      return
    }
    if (data.resumed) {
      alert(data.message || '解約予定を取り消しました。引き続きご利用いただけます。')
      const { data: user } = await supabase.auth.getUser()
      if (user?.user) await loadPaymentInfoWithUser(user.user, learner.id)
    }
  } catch (e) {
    console.error('再開エラー:', e)
    alert(e.message || '再開処理に失敗しました。')
  } finally {
    resumeLoading.value = false
  }
}

const planTypeJaLabel = (pt) =>
  pt === 'allday' ? '全日' : pt === 'weekday' ? '平日' : pt ? String(pt) : '未設定'

/** 体験中: 初回課金プランのみ変更（Stripe item price） */
const changePlanDuringTrial = async (targetPlan) => {
  const learner = currentLearner.value
  if (!learner?.id) {
    alert('お子さまを選択してください。')
    return
  }
  planSwitchLoading.value = true
  try {
    const { data: { session } } = await supabase.auth.getSession()
    const token = session?.access_token
    if (!token) {
      alert('ログインが必要です。')
      return
    }
    const base = getApiBase()
    const res = await fetch(`${base}/api/subscription/change-plan-during-trial`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ learner_id: learner.id, target_plan: targetPlan })
    })
    invalidateFamilyMembership() // 契約・体験・学習者が変わったので、家族の利用可否の結果を捨てる（指示書 B）
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      alert(data.error || 'プラン設定の更新に失敗しました。')
      return
    }
    alert(data.effective_plan_message || '設定を更新しました。')
    const { data: user } = await supabase.auth.getUser()
    if (user?.user) await loadPaymentInfoWithUser(user.user, learner.id)
  } catch (e) {
    console.error(e)
    alert(e.message || 'エラーが発生しました')
  } finally {
    planSwitchLoading.value = false
  }
}

/** 本契約: 次回請求からプラン変更 */
const changePlanNextCycle = async (targetPlan) => {
  const learner = currentLearner.value
  if (!learner?.id) {
    alert('お子さまを選択してください。')
    return
  }
  if (
    !confirm(
      `${planTypeJaLabel(targetPlan)}プランに変更しますか？\n\n※ 次回請求日から反映されます（日割り・返金はありません）。`
    )
  ) {
    return
  }
  planSwitchLoading.value = true
  try {
    const { data: { session } } = await supabase.auth.getSession()
    const token = session?.access_token
    if (!token) {
      alert('ログインが必要です。')
      return
    }
    const base = getApiBase()
    const res = await fetch(`${base}/api/subscription/change-plan-next-cycle`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ learner_id: learner.id, target_plan: targetPlan })
    })
    invalidateFamilyMembership() // 契約・体験・学習者が変わったので、家族の利用可否の結果を捨てる（指示書 B）
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      alert(data.error || 'プラン変更の登録に失敗しました。')
      return
    }
    alert(data.effective_plan_message || '登録しました。')
    const { data: user } = await supabase.auth.getUser()
    if (user?.user) await loadPaymentInfoWithUser(user.user, learner.id)
  } catch (e) {
    console.error(e)
    alert(e.message || 'エラーが発生しました')
  } finally {
    planSwitchLoading.value = false
  }
}

/** 解約済み: 新規 Checkout（トライアルなし・選択プラン） */
const restartSubscriptionWithPlan = async (targetPlan) => {
  const learner = currentLearner.value
  if (!learner?.id) {
    alert('お子さまを選択してください。')
    return
  }
  if (
    !confirm(
      `${planTypeJaLabel(targetPlan)}で再開しますか？\n\n※ 新規契約となり無料体験はつきません。お支払い画面に進みます。`
    )
  ) {
    return
  }
  resumeLoading.value = true
  try {
    const { data: { session } } = await supabase.auth.getSession()
    const token = session?.access_token
    if (!token) {
      alert('ログインが必要です。')
      return
    }
    const base = getApiBase()
    const res = await fetch(`${base}/api/subscription/restart`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ learner_id: learner.id, target_plan: targetPlan })
    })
    invalidateFamilyMembership() // 契約・体験・学習者が変わったので、家族の利用可否の結果を捨てる（指示書 B）
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      alert(data.error || '再開用の決済画面を開けませんでした。')
      return
    }
    const url = data.checkoutUrl || data.url
    if (url) {
      window.location.href = url
      return
    }
    alert(data.effective_plan_message || '処理を完了できませんでした。')
  } catch (e) {
    console.error(e)
    alert(e.message || 'エラーが発生しました')
  } finally {
    resumeLoading.value = false
  }
}

// 解約リンク発行 → Supabase Reauthentication でメール送信（Resend SMTP経由）
// item: { learnerId, learnerName, planName, currentPeriodEnd }（learnerId が null のときは従来の1契約）
const requestCancellationLinkAndOpen = async (item) => {
  try {
    console.log('📧 解約用メール送信開始（Supabase Reauthentication）', item?.learnerId ? { learnerId: item.learnerId } : {})

    const label = item?.learnerName ? `${item.learnerName}の契約` : '契約'
    const billingDate = item?.currentPeriodEnd || paymentInfo.value.nextBillingDate || paymentInfo.value.currentPeriodEnd
    const billingDateLabel =
      paymentInfo.value.subscriptionStatus === 'trialing'
        ? paymentInfo.value.isAppTrial
          ? '無料体験終了日'
          : 'トライアル終了日'
        : '次回請求日'

    const finalConfirm = confirm(
      `${label}を解約しますか？\n\n` +
      `⚠️ 重要事項:\n` +
      `• ${billingDateLabel}（${billingDate ? formatDate(billingDate) : '不明'}）までサービスを利用できます\n` +
      `• 解約後の再開は新規登録が必要です\n` +
      `• データは削除されませんが、アクセスできなくなります`
    )

    if (!finalConfirm) {
      return
    }

    const { data: { session } } = await supabase.auth.getSession()
    const email = session?.user?.email
    if (!email) {
      alert('認証が必要です。ログインしてください。')
      return
    }

    cancelLoading.value = true

    const base = `${window.location.origin}/cancel-subscription`
    const redirectTo = item?.learnerId ? `${base}?learner_id=${item.learnerId}` : base
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: redirectTo
      }
    })

    if (error) {
      throw new Error(error.message)
    }

    alert(`📧 解約手続き用リンクをメールで送信しました。\n\n宛先: ${email}\n有効期限: 1時間\n\nメールのリンクから解約ページに進んでください。`)

  } catch (error) {
    console.error('❌ 解約用メール送信エラー:', error)
    alert(`解約用メールの送信に失敗しました: ${error.message}`)
  } finally {
    cancelLoading.value = false
  }
}

// 解約リンク表示モーダル（クリップボードが使えない場合）
const showCancellationLinkModal = (link, email, expiresIn) => {
  const modalHtml = `
    <div style="position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 9999;">
      <div style="background: white; padding: 30px; border-radius: 12px; max-width: 500px; width: 90%;">
        <h3 style="margin: 0 0 20px 0; color: #dc2626;">🔗 解約手続きリンク</h3>
        <p><strong>アカウント:</strong> ${email}</p>
        <p><strong>有効期限:</strong> ${expiresIn}</p>
        <div style="background: #f9fafb; padding: 15px; border: 1px solid #e5e7eb; border-radius: 6px; margin: 20px 0;">
          <p style="margin: 0 0 10px 0; font-weight: 500;">解約手続きリンク:</p>
          <input type="text" value="${link}" readonly style="width: 100%; padding: 8px; border: 1px solid #d1d5db; border-radius: 4px; font-size: 12px;" onclick="this.select()">
        </div>
        <p style="color: #6b7280; font-size: 14px;">⚠️ このリンクをコピーして安全な場所に保存してください。24時間で無効になります。</p>
        <div style="text-align: center; margin-top: 20px;">
          <button onclick="window.open('${link}', '_blank')" style="background: #dc2626; color: white; border: none; padding: 10px 20px; border-radius: 6px; margin-right: 10px; cursor: pointer;">今すぐ開く</button>
          <button onclick="this.closest('div').remove()" style="background: #6b7280; color: white; border: none; padding: 10px 20px; border-radius: 6px; cursor: pointer;">閉じる</button>
        </div>
      </div>
    </div>
  `
  
  const modalElement = document.createElement('div')
  modalElement.innerHTML = modalHtml
  document.body.appendChild(modalElement)
}

// 支払い履歴用ヘルパー関数
const formatDate = (dateString) => {
  if (!dateString) return '不明'
  try {
    return new Date(dateString).toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    })
  } catch (error) {
    return '不明'
  }
}

const resolvePaymentPeriodStart = (payment) => {
  return payment?.service_period_start || payment?.billing_period_start || null
}

const resolvePaymentPeriodEnd = (payment) => {
  return payment?.service_period_end || payment?.billing_period_end || null
}

const getStatusText = (status) => {
  const statusMap = {
    'succeeded': '✅ 成功',
    'paid': '✅ 支払い済み', 
    'pending': '⏳ 処理中',
    'failed': '❌ 失敗',
    'canceled': '❌ キャンセル',
    'refunded': '🔄 返金済み'
  }
  return statusMap[status] || `❓ ${status}`
}

// プラン機能の表示テキスト変換
const getFeatureDisplayText = (key, value) => {
  const featureMap = {
    'feedback_access': value ? '✅ フィードバック機能' : '❌ フィードバックなし',
    'line_notifications': value ? '✅ LINE通知対応' : '❌ LINE通知なし',
    'priority_feedback': value ? '✅ 優先フィードバック' : '❌ 優先フィードバックなし',
    'feedback_guarantee': `⏰ ${value}フィードバック確約`,
    'expert_teacher': value ? '👨‍🏫 オトエイゴ上級講師確約' : '❌ 上級講師なし',
    'support_level': value === 'premium' ? '🌟 プレミアムサポート' : '📝 標準サポート'
  }
  return featureMap[key] || `${key}: ${value}`
}

// 兄弟追加の決済キャンセル後など、サブスクのないお子さま用の決済へ遷移
const checkoutForLearnerLoading = ref(false)
const goToCheckoutForCurrentLearner = async (targetPlan = 'weekday') => {
  const learner = currentLearner.value
  if (!learner?.id) {
    alert('お子さまを選択してください。')
    return
  }
  const tp = targetPlan === 'allday' ? 'allday' : 'weekday'
  if (
    !confirm(
      `${planTypeJaLabel(tp)}プランでお支払いに進みますか？`
    )
  ) {
    return
  }
  checkoutForLearnerLoading.value = true
  try {
    const { data: { session } } = await supabase.auth.getSession()
    const res = await fetch(`${getApiBase()}/api/stripe/checkout-upgrade-for-learner`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session?.access_token}`
      },
      body: JSON.stringify({ learner_id: learner.id, target_plan: tp })
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      alert(data.error || 'お支払い画面の取得に失敗しました')
      return
    }
    if (data.alreadySubscribed) {
      await loadPaymentInfo(learner.id)
      return
    }
    if (data.url) {
      window.location.href = data.url
      return
    }
    alert('お支払い画面の取得に失敗しました')
  } catch (err) {
    console.error('決済画面取得エラー:', err)
    alert('エラーが発生しました: ' + (err.message || err))
  } finally {
    checkoutForLearnerLoading.value = false
  }
}

// 支払い方法変更（パスワード再認証版）
const changePaymentMethodNew = () => {
  requirePasswordAuth('change_payment_method')
}

// 支払い方法変更実行（認証後）
const executeChangePaymentMethod = async () => {
  try {
    console.log('🔗 支払方法変更を開始します（認証済み）')
    
    const { data: { session } } = await supabase.auth.getSession()
    const token = session?.access_token
    
    if (!token) {
      alert('認証が必要です。ログインしてください。')
      return
    }

    // Customer Portal セッション作成
    const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/create-customer-portal`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || '支払方法変更に失敗しました')
    }

    const data = await response.json()
    
    if (data.success && data.url) {
      console.log('✅ Customer Portal URL取得成功:', data.url)
      
      // Stripe Customer Portal を新しいタブで開く
      window.open(data.url, '_blank', 'noopener,noreferrer')
      
      // 成功メッセージを表示
      alert('💳 Stripe Customer Portal を開きました。\n新しいタブで支払方法を変更できます。\n変更完了後、このページに戻ってきてください。')
      
    } else {
      throw new Error('Customer Portal URLの取得に失敗しました')
    }

  } catch (error) {
    console.error('❌ 支払方法変更エラー:', error)
    alert(`支払方法変更でエラーが発生しました: ${error.message}`)
  }
}


// 学習者一覧を読み込み（兄弟対応: 全件取得）
const loadLearnerInfo = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user?.id) return

    const { data, error } = await supabase
      .from('learners')
      .select('*')
      .eq('parent_id', user.id)
      .eq('type', 'child')
      .order('created_at', { ascending: true })

    if (error) {
      console.error('学習者情報読み込みエラー:', error)
      learnersList.value = []
      learnerInfo.value = null
      return
    }

    learnersList.value = data || []
    if (learnersList.value.length === 1) {
      learnerInfo.value = learnersList.value[0]
      learnerForm.value = {
        name: learnerInfo.value.name || '',
        name_kana: learnerInfo.value.name_kana || '',
        birth_date: learnerInfo.value.birth_date || ''
      }
    } else if (learnersList.value.length === 0) {
      learnerInfo.value = null
    }
    // 2人以上のときは learnerInfo は編集時にセット
  } catch (error) {
    console.error('学習者情報読み込みエラー:', error)
  }
}

// 学習者情報を読み込む関数（ユーザー情報を引数で受け取る）
const loadLearnerInfoWithUser = async (user) => {
  try {
    if (!user?.id) return

    const { data, error } = await supabase
      .from('learners')
      .select('*')
      .eq('parent_id', user.id)
      .eq('type', 'child')
      .order('created_at', { ascending: true })

    if (error) {
      console.error('学習者情報読み込みエラー:', error)
      learnersList.value = []
      learnerInfo.value = null
      return
    }

    learnersList.value = data || []
    if (learnersList.value.length === 1) {
      learnerInfo.value = learnersList.value[0]
      learnerForm.value = {
        name: learnerInfo.value.name || '',
        name_kana: learnerInfo.value.name_kana || '',
        birth_date: learnerInfo.value.birth_date || ''
      }
    } else if (learnersList.value.length === 0) {
      learnerInfo.value = null
    }
  } catch (error) {
    console.error('学習者情報読み込みエラー:', error)
  }
}

// ユーザープロフィール読み込み
const loadUserProfile = async (user) => {
  try {
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'https://api.otoeigo.com'
    const session = await supabase.auth.getSession()
    const accessToken = session.data.session?.access_token
    
    const response = await fetch(`${apiBaseUrl}/api/user-profile`, {
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    })
    
    console.log('🔍 プロフィールAPI結果:', response.status)
    
    if (response.ok) {
      const result = await response.json()
      if (result.success && result.data) {
        userProfile.value = result.data
        await syncProfileEmailWithAuth(user, result.data.email)
        console.log('✅ プロフィール設定完了:', result.data)
      } else {
        console.warn('⚠️ プロフィールAPI: データなし')
        await createFallbackProfile(user)
        await syncProfileEmailWithAuth(user, userProfile.value?.email)
      }
    } else {
      console.warn('⚠️ プロフィールAPI失敗:', response.status)
      await createFallbackProfile(user)
      await syncProfileEmailWithAuth(user, userProfile.value?.email)
    }
  } catch (error) {
    console.error('❌ プロフィールAPI例外:', error)
    await createFallbackProfile(user)
    await syncProfileEmailWithAuth(user, userProfile.value?.email)
  }
}

// LINE連携情報読み込み
const loadLineInfo = async (user) => {
  try {
    const { data: line, error: lineError } = await supabase
      .from('line_integrations')
      .select('*')
      .eq('user_id', user.id)
      
    console.log('🔍 LINE連携情報取得結果:', { line, lineError })
      
    if (line) {
      lineInfo.value = {
        parentLinked: line.some(l => l.relationship_type === 'parent'),
        learnerLinked: line.some(l => l.relationship_type === 'learner')
      }
      console.log('✅ LINE連携情報設定完了:', lineInfo.value)
    }
  } catch (error) {
    console.error('❌ LINE連携情報読み込みエラー:', error)
  }
}

const todayYmdJst = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'Asia/Tokyo' })

const shouldResyncOnResume = () => {
  const now = Date.now()
  const ymd = todayYmdJst()
  if (!lastResyncAt.value) return true
  if (ymd !== lastResyncYmd.value) return true
  return (now - lastResyncAt.value) >= RESYNC_INTERVAL_MS
}

const markResynced = () => {
  lastResyncAt.value = Date.now()
  lastResyncYmd.value = todayYmdJst()
}

const resyncOnResume = async () => {
  if (isResyncing || !shouldResyncOnResume()) return
  isResyncing = true
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user?.id) return

    await Promise.all([
      loadLearnerInfoWithUser(user),
      loadUserProfile(user),
      loadLineInfo(user)
    ])

    if (activeTab.value && activeTab.value !== 'add') {
      await loadPaymentInfoWithUser(user, activeTab.value)
    }
    await loadSubscriptionList()
    markResynced()
  } catch (e) {
    console.warn('MyPage resume resync error:', e)
  } finally {
    isResyncing = false
  }
}

const handleVisibilityChange = () => {
  if (document.visibilityState === 'visible') {
    resyncOnResume()
  }
}

const handleWindowFocus = () => {
  resyncOnResume()
}

onMounted(async () => {
  try {
    const savedSections = localStorage.getItem('mypage-sections')
    if (savedSections) {
      try {
        expandedSections.value = JSON.parse(savedSections)
      } catch (error) {
        console.log('セクション状態の復元に失敗:', error)
      }
    }

    const { data: { user } } = await supabase.auth.getUser()
    console.log('🔍 認証ユーザー:', user)

    if (user) {
      await Promise.all([
        loadLearnerInfoWithUser(user),
        loadUserProfile(user),
        loadLineInfo(user)
      ])
      await loadSubscriptionList()
    }

    // タブ初期選択: 兄弟追加決済後の戻り or 1人目 or 兄弟追加
    const addedId = route.query.added_learner_id
    if (addedId && learnersList.value.some((l) => l.id === addedId)) {
      activeTab.value = addedId
      router.replace({ path: route.path, query: {} })
    } else if (learnersList.value.length > 0) {
      activeTab.value = learnersList.value[0].id
    } else {
      activeTab.value = 'add'
    }

    // グローバルの「選択中の子」とタブ選択を同期（Top/Practice 側のアクセス制御と一致させる）
    if (activeTab.value && activeTab.value !== 'add') {
      setSelectedLearner(activeTab.value)
    } else {
      // 学習者タブが無い場合は選択解除
      setSelectedLearner(null)
    }

    // 選択タブが学習者のときその子の支払い情報を読み込み
    if (activeTab.value && activeTab.value !== 'add') {
      await loadPaymentInfoWithUser(user, activeTab.value)
    }

    checkLineAuthStatus()
    markResynced()
    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('focus', handleWindowFocus)
  } catch (error) {
    console.error('❌ データ取得エラー:', error)
  } finally {
    isLoading.value = false
  }
})

onUnmounted(() => {
  document.removeEventListener('visibilitychange', handleVisibilityChange)
  window.removeEventListener('focus', handleWindowFocus)
})

// タブ切り替え時: その子の支払い情報を読み込み & グローバル選択 learner と同期
// 初回は activeTab が null → 子ID への変更で watch が走るが、支払い取得は onMounted が既に行うため二重になる。oldTab===null のときはスキップ。
watch(activeTab, async (newTab, oldTab) => {
  if (newTab && newTab !== 'add') {
    setSelectedLearner(newTab)
    if (oldTab === null) return
    const { data: { user } } = await supabase.auth.getUser()
    if (user) await loadPaymentInfoWithUser(user, newTab)
  } else {
    setSelectedLearner(null)
  }
})

watch(selectedLearnerId, () => {
  previewInvoiceResult.value = null
  previewInvoiceError.value = ''
})

// 学習者情報編集開始（1人のときは learnerInfo を、兄弟のときは引数 l を編集対象に）
const editLearnerInfo = (l) => {
  const target = l ?? learnerInfo.value
  if (target) {
    learnerInfo.value = target
    learnerForm.value = {
      name: target.name || '',
      name_kana: target.name_kana || '',
      birth_date: target.birth_date || ''
    }
  }
  showLearnerForm.value = true
}

// 兄弟を追加（learner 登録 → アプリ内無料体験へ）
const submitAddSibling = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user?.id) return
    const name = (addSiblingForm.value.name || '').trim()
    const name_kana = (addSiblingForm.value.name_kana || '').trim()
    const birth_date = (addSiblingForm.value.birth_date || '').trim()
    if (!name || !name_kana) {
      alert('子氏名とふりがなは必須です')
      return
    }
    if (!birth_date) {
      alert('生年月日は必須です')
      return
    }
    const grade = getGradeFromBirthDate(birth_date)
    const { data: inserted, error } = await supabase
      .from('learners')
      .insert({
        parent_id: user.id,
        type: 'child',
        name,
        name_kana,
        birth_date,
        grade: grade || null,
        current_day: 1
      })
      .select('id')
      .single()
    if (error) throw error
    invalidateFamilyMembership() // 契約・体験・学習者が変わったので、家族の利用可否の結果を捨てる（指示書 B）
    if (!inserted?.id) {
      alert('学習者の登録に失敗しました')
      return
    }

    addSiblingForm.value = { name: '', name_kana: '', birth_date: '' }

    // 兄弟追加は learner ごとに「まずはアプリ内無料体験（カード不要）」で開始
    const { data: { session } } = await supabase.auth.getSession()
    const res = await fetch(`${getApiBase()}/api/app-trial/start-for-learner`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session?.access_token}`
      },
      body: JSON.stringify({ learner_id: inserted.id, flow: 'add_sibling' })
    })
    invalidateFamilyMembership() // 契約・体験・学習者が変わったので、家族の利用可否の結果を捨てる（指示書 B）
    const data = await res.json().catch(() => ({}))
    if (!res.ok || !data.success) {
      alert(data.error || '無料体験の開始に失敗しました')
      await loadLearnerInfo()
      return
    }

    // learners 一覧と支払い情報を更新して、追加した子を表示
    await loadLearnerInfo()
    activeTab.value = inserted.id
    setSelectedLearner(inserted.id)
  } catch (err) {
    console.error('兄弟追加エラー:', err)
    alert('追加に失敗しました: ' + (err.message || err))
  }
}

const saveLearnerInfo = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user?.id) {
      throw new Error('ユーザーが認証されていません')
    }

    const birth_date = learnerForm.value.birth_date || null
    const learnerData = {
      parent_id: user.id,
      name: learnerForm.value.name,
      name_kana: learnerForm.value.name_kana,
      birth_date,
      type: 'child',
      ...(birth_date && { grade: getGradeFromBirthDate(birth_date) })
    }

    let result
    if (learnerInfo.value?.id) {
      // 既存の学習者情報を更新
      result = await supabase
        .from('learners')
        .update(learnerData)
        .eq('id', learnerInfo.value.id)
        .eq('parent_id', user.id)
        .select()
        .single()
    } else {
      // 新規学習者情報を作成
      result = await supabase
        .from('learners')
        .insert(learnerData)
        .select()
        .single()
    }

    if (result.error) {
      throw result.error
    }
    invalidateFamilyMembership() // 契約・体験・学習者が変わったので、家族の利用可否の結果を捨てる（指示書 B）

    // 成功時の処理
    learnerInfo.value = result.data
    showLearnerForm.value = false
    alert('学習者情報を保存しました！')
    
  } catch (error) {
    console.error('保存エラー:', error)
    alert(`保存に失敗しました: ${error.message}`)
  }
}



const unlinkLine = (type) => {
  if (confirm(`${type === 'parent' ? '保護者' : '学習者'}のLINE連携を解除しますか？`)) {
    // データベースからLINE連携情報を削除
    removeLineIntegration(type)
  }
}

const openAccountForm = () => {
  accountForm.value = {
    full_name: userProfile.value?.full_name || '',
    email: userProfile.value?.email || ''
  }
  showAccountForm.value = true
}

const openPasswordChangeForm = () => {
  passwordChangeForm.value = { password: '', confirmPassword: '' }
  showPasswordChangeForm.value = true
}

const savePasswordChange = async () => {
  try {
    if (passwordChangeSaving.value) return
    passwordChangeSaving.value = true

    const password = (passwordChangeForm.value.password || '').trim()
    const confirmPassword = (passwordChangeForm.value.confirmPassword || '').trim()
    if (password.length < 8) {
      alert('パスワードは8文字以上で入力してください。')
      return
    }
    if (password !== confirmPassword) {
      alert('確認用パスワードが一致しません。')
      return
    }

    const { error } = await supabase.auth.updateUser({ password })
    if (error) throw error

    showPasswordChangeForm.value = false
    alert('パスワードを変更しました。')
  } catch (error) {
    console.error('パスワード変更エラー:', error)
    alert(`パスワード変更に失敗しました: ${error.message}`)
  } finally {
    passwordChangeSaving.value = false
  }
}

const saveAccountInfo = async () => {
  try {
    if (accountSaving.value) return
    accountSaving.value = true
    const fullName = (accountForm.value.full_name || '').trim()
    const newEmail = (accountForm.value.email || '').trim()
    if (!fullName || !newEmail) {
      alert('お名前とメールアドレスを入力してください。')
      return
    }

    const { data: { user } } = await supabase.auth.getUser()
    if (!user?.id) {
      throw new Error('ユーザーが認証されていません')
    }

    // 1) 契約者名を即時更新（users_phase2）
    const { error: nameError } = await supabase
      .from('users_phase2')
      .update({
        full_name: fullName,
        updated_at: new Date().toISOString()
      })
      .eq('id', user.id)
    if (nameError) throw nameError

    // 2) メール変更（Supabase Auth標準フロー）
    const currentEmail = (userProfile.value?.email || user.email || '').trim()
    const emailChanged = newEmail.toLowerCase() !== currentEmail.toLowerCase()
    if (emailChanged) {
      const { error: emailError } = await supabase.auth.updateUser(
        { email: newEmail },
        { emailRedirectTo: `${window.location.origin}/mypage` }
      )
      if (emailError) throw emailError
    }

    // 画面表示を更新
    userProfile.value = {
      ...(userProfile.value || {}),
      full_name: fullName,
      // メール変更時は承認前のため現表示は維持。未変更なら入力値で更新しておく
      email: emailChanged ? (userProfile.value?.email || currentEmail) : newEmail
    }
    showAccountForm.value = false

    if (emailChanged) {
      alert('アカウント情報を保存しました。メールアドレス変更の確認メールを送信しました。\n旧メール・新メールの両方に届く場合は、両方の承認が完了すると反映されます。')
    } else {
      alert('アカウント情報を保存しました。')
    }

    // API由来の表示値（full_name等）を再同期
    await loadUserProfile(user)
  } catch (error) {
    console.error('アカウント情報更新エラー:', error)
    alert(`アカウント情報の更新に失敗しました: ${error.message}`)
  } finally {
    accountSaving.value = false
  }
}

// LINE連携情報の削除
const removeLineIntegration = async (type) => {
  try {
    // ユーザーIDの安全な取得
    let userId = null
    
    if (userProfile.value?.id) {
      userId = userProfile.value.id
    } else {
      // userProfileが未設定の場合、認証ユーザーから直接取得
      const { data: { user } } = await supabase.auth.getUser()
      if (!user?.id) {
        throw new Error('ユーザーが認証されていません')
      }
      userId = user.id
    }
    
    // 1. line_integrationsから削除
    const { error: integrationError } = await supabase
      .from('line_integrations')
      .delete()
      .eq('user_id', userId)
      .eq('relationship_type', type)
    
    if (integrationError) throw integrationError
    
    // 2. line_link_codesのline_user_idも削除（連携コードを再利用可能にする）
    // relationship_type → line_link_codes.type の逆マッピング
    const linkCodeType = type === 'learner' ? 'self' : type // 'learner' → 'self', 'parent' → 'parent'
    
    console.log('🔍 デバッグ情報:', { userId, type, linkCodeType })
    
    // 更新前の状態を確認
    const { data: beforeUpdate, error: beforeError } = await supabase
      .from('line_link_codes')
      .select('id, code, type, line_user_id, used_at')
      .eq('user_id', userId)
      .eq('type', linkCodeType)
    
    console.log('📊 更新前のline_link_codes:', beforeUpdate)
    
    const { error: linkCodeError, data: updateResult } = await supabase
      .from('line_link_codes')
      .update({
        line_user_id: null,
        used_at: null
      })
      .eq('user_id', userId)
      .eq('type', linkCodeType)
      .select()
    
    console.log('📊 更新結果:', { updateResult, linkCodeError })
    
    if (linkCodeError) {
      console.error('❌ line_link_codesの更新に失敗:', linkCodeError)
      alert(`連携解除は完了しましたが、コードの再利用設定に失敗しました: ${linkCodeError.message}`)
    } else {
      console.log('✅ line_link_codesの更新成功')
    }
    
    // ローカル状態を更新
    lineInfo.value[`${type}Linked`] = false
    alert('LINE連携を解除しました。連携コードは再利用可能になりました。')
  } catch (error) {
    console.error('LINE連携解除エラー:', error)
    alert('LINE連携解除に失敗しました')
  }
}

// LINEログイン方式での連携開始関数
const startLineLinking = async (type) => {
  console.log(`🚀 ${type}のLINE連携を開始します...`)
  
  try {
    // ユーザーIDの安全な取得
    let userId = null
    
    if (userProfile.value?.id) {
      userId = userProfile.value.id
    } else {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user?.id) {
        throw new Error('ユーザーが認証されていません')
      }
      userId = user.id
    }
    
    // LINEログイン認証URLを生成
    // stateにユーザーIDをエンコードして含める（セキュリティ向上）
    const userData = JSON.stringify({ userId, type })
    const state = btoa(userData) // base64エンコード
    const nonce = generateRandomString()
    
    // 状態を保存（コールバック時に使用）
    localStorage.setItem('line_auth_state', state)
    localStorage.setItem('line_auth_nonce', nonce)
    localStorage.setItem('line_auth_type', type)
    localStorage.setItem('line_auth_user_id', userId)
    
    // LINE認証URLを構築
    const lineLoginChannelId = import.meta.env.VITE_LINE_LOGIN_CHANNEL_ID || 'your_line_login_channel_id'
    const redirectUri = `${import.meta.env.VITE_API_BASE_URL || 'https://api.otoeigo.com'}/api/line-callback`
    
    const authUrl = `https://access.line.me/oauth2/v2.1/authorize?response_type=code&client_id=${lineLoginChannelId}&redirect_uri=${encodeURIComponent(redirectUri)}&state=${state}&scope=openid%20profile&nonce=${nonce}&bot_prompt=aggressive`
    
    console.log('🔗 LINE認証URL:', authUrl)
    console.log('📱 認証ページに遷移します...')
    
    // LINE認証ページに遷移
    window.location.href = authUrl
    
  } catch (error) {
    console.error('LINE連携開始エラー:', error)
    alert('LINE連携の開始に失敗しました: ' + error.message)
  }
}

// 既存友達としての連携試行
const tryExistingFriendLink = async (type) => {
  try {
    // ユーザーIDの安全な取得
    let userId = null
    
    if (userProfile.value?.id) {
      userId = userProfile.value.id
    } else {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user?.id) {
        throw new Error('ユーザーが認証されていません')
      }
      userId = user.id
    }
    
    // 既存友達のLINE IDを自動取得
    const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/line-link-existing`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        type: type,
        userId: userId
      })
    })
    
    if (!response.ok) {
      console.error('HTTPエラー:', response.status, response.statusText)
      return false
    }
    
    const result = await response.json()
    console.log('既存友達連携APIレスポンス:', result)
    
    if (result.success) {
      // 既存友達として連携完了
      console.log(`✅ ${type}の既存友達連携が完了しました！`)
      return true
    }
    
    return false
  } catch (error) {
    console.error('既存友達連携試行エラー:', error)
    return false
  }
}

// ランダム文字列生成関数
const generateRandomString = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  let result = ''
  for (let i = 0; i < 32; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return result
}

// LINE認証後の状態確認
const checkLineAuthStatus = () => {
  const urlParams = new URLSearchParams(window.location.search)
  const lineStatus = urlParams.get('line_status')
  const message = urlParams.get('message')
  
  if (lineStatus) {
    console.log('📱 LINE認証結果:', { lineStatus, message })
    
    if (lineStatus === 'success') {
      alert('✅ LINE連携が完了しました！')
      // ページをリロードして状態を更新
      window.location.href = '/mypage'
    } else if (lineStatus === 'pending') {
      alert('⏳ LINE連携は完了しましたが、友だち追加が必要です。LINEアプリで友だち追加を完了してください。')
      // ページをリロードして状態を更新
      window.location.href = '/mypage'
    } else if (lineStatus === 'error') {
      alert(`❌ LINE連携に失敗しました: ${message || '不明なエラー'}`)
    }
  }
}



// 自動検出の開始
const startAutoDetection = (type) => {
  console.log(`🔍 ${type}のLINE連携を自動検出中...`)
  
  // 成功通知の表示
  showDetectionStatus(type, '検出中...')
  
  // 3秒間隔で友達追加をチェック（より頻繁にチェック）
  const checkInterval = setInterval(async () => {
    try {
      console.log(`🔄 ${type}の友達追加をチェック中... (${new Date().toLocaleTimeString()})`)
      const detected = await checkForNewFriend(type)
      if (detected) {
        clearInterval(checkInterval)
        console.log(`✅ ${type}のLINE連携が自動完了しました！`)
        // 成功通知
        showSuccessNotification(type)
        // 状態を更新
        updateLineInfo(type)
      } else {
        console.log(`⏳ ${type}の友達追加はまだ検出されていません...`)
      }
    } catch (error) {
      console.error('自動検出エラー:', error)
    }
  }, 3000) // 3秒間隔
  
  // 2分後にタイムアウト
  setTimeout(() => {
    clearInterval(checkInterval)
    console.log(`⏰ ${type}の自動検出がタイムアウトしました`)
    // 手動確認にフォールバック
    showManualFallback(type)
  }, 120000) // 2分
}

// 新規友達のチェック
const checkForNewFriend = async (type) => {
  try {
    console.log(`🔍 ${type}の新規友達チェック開始...`)
    
    // ユーザーIDの安全な取得
    let userId = null
    
    if (userProfile.value?.id) {
      userId = userProfile.value.id
      console.log(`👤 ユーザーID (userProfile): ${userId}`)
    } else {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user?.id) {
        throw new Error('ユーザーが認証されていません')
      }
      userId = user.id
      console.log(`👤 ユーザーID (auth): ${userId}`)
    }
    
    console.log(`📡 バックエンドAPI呼び出し中: ${import.meta.env.VITE_API_BASE_URL}/api/line-detect-new-friend`)
    
    // バックエンドで友達追加後のLINE IDを自動検出
    const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/line-detect-new-friend`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        type: type,
        userId: userId
      })
    })
    
    console.log(`📡 APIレスポンス: ${response.status} ${response.statusText}`)
    
    if (!response.ok) {
      const errorText = await response.text()
      console.error(`❌ APIエラー: ${response.status} ${response.statusText}`)
      console.error(`❌ エラー詳細: ${errorText}`)
      throw new Error(`HTTP ${response.status}: ${response.statusText}`)
    }
    
    const result = await response.json()
    console.log(`📊 自動検出APIレスポンス:`, result)
    
    if (result.success && result.lineUserId) {
      // 自動検出成功
      console.log(`✅ LINE ID自動検出成功: ${result.lineUserId}`)
      
      // LINE連携情報を保存
      await saveLineIntegration(type, result.lineUserId)
      
      return true
    } else {
      console.log(`⚠️ 自動検出失敗: ${result.message || '不明なエラー'}`)
    }
    
    return false
  } catch (error) {
    console.error(`❌ 新規友達チェックエラー:`, error)
    return false
  }
}

// 検出状態の表示
const showDetectionStatus = (type, status) => {
  const message = `${type === 'parent' ? '保護者' : '学習者'}のLINE連携を自動検出中...`
  console.log(message)
  
  // ユーザーに検出中であることを通知（静かに）
  // 必要に応じてUIに状態を表示
}

// 成功通知の表示
const showSuccessNotification = (type) => {
  alert(`${type === 'parent' ? '保護者' : '学習者'}のLINE連携が完了しました！\n\n✅ 連携が完了\n✅ フィードバック通知を受信可能\n\n連携の解除や変更は、このページからいつでも可能です。`)
}

// 手動フォールバックの表示
const showManualFallback = (type) => {
  const message = `自動検出がタイムアウトしました。\n\n${type === 'parent' ? '保護者' : '学習者'}のLINE IDを手動で入力してください。\n\n例: @username または U1234567890abcdef`
  
  const lineId = prompt(message)
  if (lineId) {
    saveLineIntegration(type, lineId)
  } else {
    alert('LINE IDが入力されませんでした。後で再度設定してください。')
  }
}

// LINE連携情報の更新
const updateLineInfo = (type) => {
  lineInfo.value[`${type}Linked`] = true
}



// 手動LINE ID入力（フォールバック）
const showManualLineIdInput = (type) => {
  const message = `自動検出に失敗しました。\n\n${type === 'parent' ? '保護者' : '学習者'}のLINE IDを手動で入力してください。\n\n例: @username または U1234567890abcdef`
  
  const lineId = prompt(message)
  if (lineId) {
    saveLineIntegration(type, lineId)
  } else {
    alert('LINE IDが入力されませんでした。後で再度設定してください。')
  }
}





// LINE連携情報の保存
const saveLineIntegration = async (type, lineId) => {
  try {
    // ユーザーIDの安全な取得
    let userId = null
    
    if (userProfile.value?.id) {
      userId = userProfile.value.id
    } else {
      // userProfileが未設定の場合、認証ユーザーから直接取得
      const { data: { user } } = await supabase.auth.getUser()
      if (!user?.id) {
        throw new Error('ユーザーが認証されていません')
      }
      userId = user.id
    }
    
    const { error } = await supabase
      .from('line_integrations')
      .upsert([
        {
          user_id: userId,
          relationship_type: type,
          line_user_id: lineId,
          status: 'active',
          created_at: new Date().toISOString()
        }
      ])
    
    if (error) throw error
    
    // ローカル状態を更新
    lineInfo.value[`${type}Linked`] = true
    alert(`${type === 'parent' ? '保護者' : '学習者'}のLINE連携が完了しました！`)
  } catch (error) {
    console.error('LINE連携保存エラー:', error)
    alert('LINE連携に失敗しました')
  }
}
</script>

<style scoped>
.mypage {
  max-width: 100%;
  margin: 0;
  padding: 0 1rem 2rem;
  background: #f8fafc;
  min-height: 100vh;
  /* OS がダークモードでも :root の白文字を継承しないよう、マイページは常にライト基調で表示 */
  color: #1f2937;
  color-scheme: light;
}

.mypage-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1rem;
  background: white;
  margin-left: -1rem;
  margin-right: -1rem;
  padding: 1rem 1rem 1.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.mypage-header {
  text-align: left;
  margin-bottom: 0;
  background: transparent;
  margin: 0;
  padding: 0;
  box-shadow: none;
}

.mypage-header-actions {
  flex-shrink: 0;
}

.add-sibling-header-btn {
  white-space: nowrap;
}

.mypage-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin-bottom: 1rem;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 0.5rem;
  /* 狭い画面で兄弟が多いときは横スクロールで隠れないように */
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
}

.mypage-tab {
  flex: 0 0 auto;
  min-height: 44px;
  padding: 0.5rem 1rem;
  border: 1px solid #cbd5e1;
  background: #ffffff;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 600;
  /* グローバル button / :root の白文字を上書き（非選択でも必ず読める色） */
  color: #0f172a;
  -webkit-tap-highlight-color: transparent;
}

.mypage-tab:hover {
  background: #f1f5f9;
  color: #0f172a;
  border-color: #94a3b8;
}

.mypage-tab:focus-visible {
  outline: 2px solid #2563eb;
  outline-offset: 2px;
}

.mypage-tab.active {
  background: #2563eb;
  color: #ffffff;
  border-color: #2563eb;
}

.mypage-tab.add-tab {
  background: #fef3c7;
  border-color: #d97706;
  color: #78350f;
}

.mypage-tab.add-tab.active {
  background: #d97706;
  color: #ffffff;
  border-color: #d97706;
}

.add-sibling-content {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1rem;
  border: 1px solid #e5e7eb;
}

.add-sibling-content .add-sibling-note {
  font-size: 0.9rem;
  color: #6b7280;
  margin: 0 0 1rem 0;
  line-height: 1.5;
}

.add-sibling-form .form-group {
  margin-bottom: 1rem;
}

.add-sibling-form .form-actions {
  margin-top: 1.5rem;
  display: flex;
  gap: 0.75rem;
}

.add-sibling-content .computed-grade {
  margin-bottom: 0.5rem;
}

.add-sibling-content .computed-grade-value {
  font-weight: 600;
  color: #111827;
}

.mypage-header h1 {
  color: #1f2937;
  margin-bottom: 0.5rem;
  font-size: 1.8rem;
}

.mypage-header p {
  color: #6b7280;
  font-size: 1rem;
  margin: 0;
}

/* 折り畳みセクション群 */
.collapsible-sections {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 2rem;
}

.section-container {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

/* セクションヘッダー（クリック可能） */
.section-header {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.25rem;
  background: white;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s;
  border-bottom: 1px solid #f3f4f6;
}

.section-header:hover {
  background: #f9fafb;
}

.section-header:active {
  background: #f3f4f6;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.section-icon {
  font-size: 1.25rem;
}

.section-text {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1f2937;
}

/* シェブロンアイコン */
.chevron {
  font-size: 0.875rem;
  color: #6b7280;
  transition: transform 0.2s ease;
}

.chevron.rotated {
  transform: rotate(180deg);
}

/* セクションコンテンツ */
.section-content {
  padding: 1.25rem;
  border-top: 1px solid #f3f4f6;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid #f3f4f6;
}

.info-row:last-child {
  border-bottom: none;
}

/* スライドアニメーション */
.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease;
  overflow: hidden;
}

.slide-enter-from,
.slide-leave-to {
  max-height: 0;
  opacity: 0;
  transform: translateY(-10px);
}

.slide-enter-to,
.slide-leave-from {
  max-height: 1000px;
  opacity: 1;
  transform: translateY(0);
}

/* LINE連携コンテンツ特別処理 */
.line-content {
  padding: 0;
}

/* 支払い情報 */
.payment-info {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

/* アラート */
.alert {
  padding: 12px;
  border-radius: 6px;
  margin-bottom: 12px;
  font-weight: 500;
}

.alert-danger {
  background-color: #fee;
  color: #c53030;
  border: 1px solid #fed7d7;
}

.alert-warning {
  background-color: #fffaf0;
  color: #dd6b20;
  border: 1px solid #fbd38d;
}

/* 支払い方法詳細 */
.payment-method-details {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.card-warning {
  font-size: 0.9em;
  color: #dd6b20;
  font-weight: 500;
}

/* プラン機能 */
.plan-features {
  border-top: 1px solid #e2e8f0;
  padding-top: 12px;
  margin-top: 12px;
}

.plan-features label {
  margin-bottom: 8px;
  display: block;
  font-weight: 600;
  color: #2d3748;
}

.features-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.features-list li {
  padding: 4px 0;
  color: #4a5568;
  font-size: 0.95em;
}

.plan-name {
  font-weight: 600;
  color: #059669;
}

.plan-line-row .plan-line-text {
  font-size: 0.95rem;
  color: #374151;
  line-height: 1.45;
}

.discount-row .discount-wrap {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.discount-badge {
  display: inline-block;
  background: #ecfdf5;
  color: #047857;
  font-weight: 700;
  font-size: 0.8rem;
  padding: 4px 10px;
  border-radius: 999px;
}

.discount-label {
  color: #374151;
  font-size: 0.9rem;
}

.muted-text {
  color: #6b7280;
  font-size: 0.95rem;
}

.price-note {
  margin: 6px 0 0 0;
  font-size: 0.8rem;
  color: #6b7280;
}

.contract-block {
  border: 1px solid #e2e8f0;
  background: #fafafa;
  border-radius: 10px;
  padding: 12px 14px;
  margin-top: 8px;
}

.preview-next-invoice-wrap {
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px dashed #e2e8f0;
}

.preview-next-invoice-btn {
  font-size: 0.88rem;
}

.preview-invoice-error {
  margin: 8px 0 0 0;
  font-size: 0.85rem;
  color: #b91c1c;
  line-height: 1.45;
}

.preview-invoice-panel {
  margin-top: 10px;
  padding: 10px 12px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.preview-invoice-note {
  margin: 0 0 8px 0;
  font-size: 0.82rem;
  color: #64748b;
  line-height: 1.45;
}

.preview-invoice-details {
  margin-top: 8px;
  font-size: 0.88rem;
}

.preview-invoice-details summary {
  cursor: pointer;
  color: #0369a1;
  font-weight: 600;
}

.preview-invoice-lines {
  margin: 8px 0 0 0;
  padding-left: 1.1rem;
  list-style: disc;
}

.preview-invoice-lines li {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin: 4px 0;
  font-size: 0.86rem;
}

.preview-line-amount {
  flex-shrink: 0;
  font-variant-numeric: tabular-nums;
}

.contract-block.membership-service-block {
  border-color: #c7d2fe;
  background: #f8fafc;
}

.membership-submit-today.is-yes {
  color: #047857;
  font-weight: 600;
}

.membership-submit-today.is-no {
  color: #b91c1c;
  font-weight: 600;
}

.membership-message-row {
  align-items: flex-start;
  flex-direction: column;
  gap: 4px;
}

.membership-message-row label {
  flex-shrink: 0;
}

.membership-message-text {
  margin: 0;
  width: 100%;
  font-size: 0.95rem;
  line-height: 1.55;
  color: #334155;
  white-space: pre-wrap;
}

.contract-block-title {
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 10px 0;
  font-size: 1rem;
}

.contract-detail-note {
  margin: 8px 0 0 0;
  font-size: 0.88rem;
  color: #64748b;
  line-height: 1.55;
}

.price {
  font-weight: 600;
  color: #dc2626;
  font-size: 1.125rem;
}

.trial-end {
  font-weight: 600;
  color: #0284c7;
}

.status {
  font-weight: 600;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.9rem;
}

.status.active {
  color: #059669;
  background: #ecfdf5;
}

.status.trialing {
  color: #0284c7;
  background: #e0f2fe;
}

.status.past_due,
.status.unpaid {
  color: #dc2626;
  background: #fef2f2;
}

.status.canceled {
  color: #6b7280;
  background: #f9fafb;
}

.loading-payment {
  text-align: center;
  padding: 2rem;
  color: #6b7280;
}

.payment-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #f3f4f6;
}

/* 独立したログアウトセクション */
.logout-section-standalone {
  text-align: center;
  padding: 1.5rem 0;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-top: 1rem;
}

.logout-btn-standalone {
  background: #f3f4f6;
  color: #6b7280;
  border: 1px solid #d1d5db;
  padding: 0.75rem 2rem;
  border-radius: 8px;
  cursor: pointer;
  font-size: 1rem;
  transition: all 0.2s;
  font-weight: 500;
}

.logout-btn-standalone:hover {
  background: #e5e7eb;
  color: #374151;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.info-row label {
  font-weight: 500;
  color: #374151;
}

.info-row span {
  color: #6b7280;
}

.no-learner {
  text-align: center;
  padding: 2rem;
}

.no-learner p {
  color: #6b7280;
  margin-bottom: 1rem;
}

.learners-list-desc {
  font-size: 0.9rem;
  color: #6b7280;
  margin-bottom: 1rem;
}

.learners-ul {
  list-style: none;
  padding: 0;
  margin: 0 0 1rem 0;
}

.learner-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.6rem 0;
  border-bottom: 1px solid #e5e7eb;
}

.learner-item:last-child {
  border-bottom: none;
}

.learner-name {
  font-weight: 500;
}

.btn-small {
  padding: 0.35rem 0.75rem;
  font-size: 0.85rem;
}

.add-sibling-btn {
  margin-top: 0.5rem;
}

.required {
  color: #dc2626;
}

.help-text {
  display: block;
  margin-top: 0.35rem;
  color: #6b7280;
  font-size: 0.82rem;
}

.line-status {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  margin-bottom: 1rem;
}

.line-buttons {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
}

.status-item h4 {
  margin: 0 0 1rem 0;
  color: #1f2937;
  font-size: 1rem;
}

.linked,
.not-linked {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: flex-start;
}

.status-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
}

.status-badge.success {
  background: #d1fae5;
  color: #065f46;
}

.status-badge.warning {
  background: #fef3c7;
  color: #92400e;
}

.line-help {
  background: #f0f9ff;
  border: 1px solid #0ea5e9;
  border-radius: 8px;
  padding: 1rem;
  margin-top: 1rem;
}

.line-help p {
  margin: 0;
  color: #0c4a6e;
  font-size: 0.9rem;
}

.btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  /* グローバル style.css の button（ダークモードで #1a1a1a 等）を打ち消す */
  font-family: inherit;
  background-color: transparent;
  color: inherit;
}

/* アウトライン系（支払い方法変更・履歴など）— モードに依存せず常に読める */
.btn-outline {
  background-color: #ffffff !important;
  color: #1e40af !important;
  border: 2px solid #2563eb !important;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
}

.btn-outline:hover {
  background-color: #eff6ff !important;
  color: #1d4ed8 !important;
  border-color: #1d4ed8 !important;
}

.btn-outline:focus-visible {
  outline: 2px solid #2563eb;
  outline-offset: 2px;
}

.btn.small {
  padding: 0.5rem 1rem;
  font-size: 0.9rem;
}

.btn-primary {
  background: #667eea;
  color: white;
}

.btn-primary:hover {
  background: #5a6fd8;
}

.btn-secondary {
  background: #6b7280;
  color: white;
}

.btn-secondary:hover {
  background: #4b5563;
}

.btn-danger {
  background: #ef4444;
  color: white;
}

.btn-danger:hover {
  background: #dc2626;
}

.learner-form-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 12px;
  padding: 2rem;
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-content h3 {
  margin: 0 0 1.5rem 0;
  color: #1f2937;
  text-align: center;
}

.add-sibling-note {
  font-size: 0.9rem;
  color: #6b7280;
  margin: -0.5rem 0 1rem 0;
  line-height: 1.5;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #374151;
}

.form-input,
.form-select {
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.2s;
}

.form-input:focus,
.form-select:focus {
  outline: none;
  border-color: #667eea;
}

.form-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
}



/* スマホ対応 */
@media (max-width: 768px) {
  .line-status {
    grid-template-columns: 1fr;
  }
  
  .form-actions {
    flex-direction: column;
  }
  
  .btn {
    width: 100%;
  }
  

}

/* 支払い履歴モーダル (debugmemo 8番) */
.payment-history-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.payment-history-content {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  overflow: hidden;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
}

.payment-history-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #e5e7eb;
  background-color: #f9fafb;
}

.payment-history-header h3 {
  margin: 0;
  color: #374151;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #6b7280;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s;
}

.close-btn:hover {
  background-color: #e5e7eb;
}

.payment-history-body {
  padding: 20px;
  max-height: 50vh;
  overflow-y: auto;
}

.no-payment-history {
  text-align: center;
  padding: 40px 20px;
}

.no-payment-history p {
  margin: 0 0 10px 0;
}

.text-muted {
  color: #6b7280;
  font-size: 14px;
}

.payment-history-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.payment-history-item {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  background-color: #f9fafb;
}

.payment-main-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  flex-wrap: wrap;
  gap: 8px;
}

.payment-date {
  font-weight: 500;
  color: #374151;
}

.payment-amount {
  font-weight: bold;
  font-size: 18px;
  color: #059669;
}

.payment-status {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.payment-status.succeeded,
.payment-status.paid {
  background-color: #d1fae5;
  color: #065f46;
}

.payment-status.pending {
  background-color: #fef3c7;
  color: #92400e;
}

.payment-status.failed,
.payment-status.canceled {
  background-color: #fee2e2;
  color: #991b1b;
}

.payment-status.refunded {
  background-color: #dbeafe;
  color: #1e40af;
}

.payment-details {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.payment-description {
  color: #6b7280;
  font-size: 14px;
}

.payment-period {
  color: #6b7280;
  font-size: 13px;
}

.invoice-link {
  color: #3b82f6;
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
}

.invoice-link:hover {
  text-decoration: underline;
}

.payment-history-footer {
  padding: 20px;
  border-top: 1px solid #e5e7eb;
  background-color: #f9fafb;
  text-align: center;
}

/* モバイル対応 */
@media (max-width: 768px) {
  .payment-history-content {
    width: 95%;
    max-height: 90vh;
  }
  
  .payment-main-info {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .payment-amount {
    font-size: 16px;
  }
}

/* プラン変更モーダル (debugmemo 9番) */
.plan-change-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.plan-change-content {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 800px;
  max-height: 85vh;
  overflow: hidden;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
}

.plan-change-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #e5e7eb;
  background-color: #f9fafb;
}

.plan-change-header h3 {
  margin: 0;
  color: #374151;
}

.plan-change-body {
  padding: 20px;
  max-height: 60vh;
  overflow-y: auto;
}

.loading-state,
.no-plans {
  text-align: center;
  padding: 40px 20px;
  color: #6b7280;
}

.plans-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

.plan-card {
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  padding: 20px;
  background-color: #f9fafb;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.plan-card:hover {
  border-color: #3b82f6;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
}

.plan-header {
  text-align: center;
  margin-bottom: 20px;
}

.plan-header h4 {
  margin: 0 0 10px 0;
  color: #374151;
  font-size: 20px;
  font-weight: 600;
}

.plan-price {
  font-size: 32px;
  font-weight: bold;
  color: #059669;
}

.price-period {
  font-size: 16px;
  color: #6b7280;
  font-weight: normal;
}

.plan-features {
  margin: 20px 0;
}

.plan-features ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.plan-features li {
  padding: 6px 0;
  color: #374151;
  font-size: 14px;
  border-bottom: 1px solid #e5e7eb;
}

.plan-features li:last-child {
  border-bottom: none;
}

.plan-actions {
  text-align: center;
  margin-top: 20px;
}

.plan-actions .btn {
  width: 100%;
  padding: 12px;
  font-weight: 600;
}

.plan-change-notice {
  background-color: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: 8px;
  padding: 16px;
  margin-top: 20px;
}

.plan-change-notice p {
  margin: 0 0 8px 0;
  color: #92400e;
  font-weight: 600;
}

.plan-change-notice ul {
  margin: 0;
  padding-left: 20px;
  color: #92400e;
}

.plan-change-notice li {
  margin-bottom: 4px;
  font-size: 14px;
}

.plan-change-footer {
  padding: 20px;
  border-top: 1px solid #e5e7eb;
  background-color: #f9fafb;
  text-align: center;
}

/* プラン変更モーダル - モバイル対応 */
@media (max-width: 768px) {
  .plan-change-content {
    width: 95%;
    max-height: 90vh;
  }
  
  .plans-grid {
    grid-template-columns: 1fr;
    gap: 15px;
  }
  
  .plan-card {
    padding: 15px;
  }
  
  .plan-price {
    font-size: 28px;
  }
}

/* 解約モーダル (debugmemo 10番) */
.cancel-modal {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 16px;
}

.cancel-content {
  background: #fff;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  border-radius: 20px;
  overflow-y: auto;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
}

.cancel-header {
  padding: 20px;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  background: #fff;
  z-index: 10;
}

.cancel-header .modal-title {
  font-size: 1.1rem;
  font-weight: bold;
  margin: 0;
  color: #333;
}

.cancel-header .close-btn {
  background: none;
  border: none;
  font-size: 28px;
  color: #999;
  padding: 0 8px;
  line-height: 1;
  cursor: pointer;
}

.cancel-body {
  padding-bottom: 30px;
}

.cancel-warning {
  background: #fff9f0;
  border: 1px solid #ffeeba;
  margin: 20px;
  padding: 16px;
  border-radius: 12px;
}

.warning-title {
  color: #856404;
  margin: 0 0 8px 0;
  font-size: 1rem;
}

.warning-intro {
  font-size: 0.9rem;
  margin-bottom: 12px;
  color: #666;
}

.warning-list {
  margin: 0;
  padding-left: 20px;
  font-size: 0.85rem;
  color: #666;
  line-height: 1.5;
}

.warning-list li {
  margin-bottom: 6px;
}

.cancel-form .form-group {
  padding: 0 20px;
  margin-bottom: 24px;
}

.cancel-form .form-label {
  display: block;
  font-weight: bold;
  font-size: 0.95rem;
  margin-bottom: 10px;
  color: #444;
  line-height: 1.4;
}

.cancel-form .optional-tag {
  font-size: 0.75rem;
  background: #eee;
  padding: 2px 6px;
  border-radius: 4px;
  margin-left: 6px;
  font-weight: normal;
  color: #777;
}

.cancel-form .select-wrapper {
  display: block;
}

.cancel-form .form-control {
  width: 100%;
  height: 50px;
  padding: 0 12px;
  border: 1.5px solid #ddd;
  border-radius: 10px;
  font-size: 1rem;
  background-color: #f9f9f9;
  appearance: none;
  color: #111827; /* スマホ（特にiOS）で文字色が薄くならないよう固定 */
  -webkit-text-fill-color: #111827;
}

.cancel-form select.form-control:invalid {
  /* 「選択してください」（valueが空の状態）を必ず見える色に */
  color: #6b7280;
  -webkit-text-fill-color: #6b7280;
}

.cancel-form .form-control option {
  color: #111827;
}

.cancel-form .form-control:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.15);
}

.cancel-form .form-textarea {
  width: 100%;
  padding: 12px;
  border: 1.5px solid #ddd;
  border-radius: 10px;
  font-size: 1rem;
  background-color: #f9f9f9;
  line-height: 1.5;
  resize: vertical;
  min-height: 100px;
}

.cancel-form .form-textarea:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.15);
}

.cancel-actions {
  display: flex;
  flex-direction: column-reverse;
  gap: 12px;
  padding: 0 20px;
  margin-top: 10px;
}

.btn-secondary-full,
.btn-danger-full {
  width: 100%;
  height: 54px;
  border-radius: 27px;
  font-size: 1rem;
  font-weight: bold;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.btn-secondary-full {
  background: #f0f0f0;
  color: #666;
}

.btn-secondary-full:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-danger-full {
  background: #ff4d4f;
  color: #fff;
}

.btn-danger-full:hover:not(:disabled) {
  background: #e64547;
}

.btn-danger-full:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.animate-fade-in {
  animation: cancelModalFadeIn 0.3s ease-out;
}

@keyframes cancelModalFadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* パスワード再認証モーダル（セキュリティ強化版） */
.password-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
}

.password-content {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 450px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  border: 2px solid #3b82f6;
}

.password-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #e5e7eb;
  background-color: #eff6ff;
}

.password-header h3 {
  margin: 0;
  color: #1e40af;
  font-weight: 600;
}

.password-body {
  padding: 20px;
}

.security-notice {
  background-color: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
}

.security-notice h4 {
  margin: 0 0 8px 0;
  color: #92400e;
  font-size: 16px;
  font-weight: 600;
}

.security-notice p {
  margin: 0;
  color: #92400e;
  font-size: 14px;
  line-height: 1.5;
}

.password-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.password-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.password-actions .btn {
  min-width: 80px;
  padding: 10px 16px;
  font-weight: 500;
}

.btn-primary {
  background-color: #3b82f6;
  color: white;
  border: 1px solid #3b82f6;
}

.btn-primary:hover:not(:disabled) {
  background-color: #2563eb;
  border-color: #2563eb;
}

.btn-primary:disabled {
  background-color: #9ca3af;
  border-color: #9ca3af;
  cursor: not-allowed;
}

/* パスワードモーダル - モバイル対応 */
@media (max-width: 768px) {
  .password-content {
    width: 95%;
  }
  
  .password-actions {
    flex-direction: column;
  }
  
  .password-actions .btn {
    width: 100%;
  }
}
</style>
